/* eslint-disable */
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { saveToStorage, loadFromStorage } from "./Auth";
import { getSupabaseClient } from "./Auth";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — INTERACTIVE TACTICS BOARD
   Drag & drop players · Formation selector · Role assignment
   Drill attachment · Arrows & annotations · Export ready
   Moonrise Kingdom palette · Bebas Neue + DM Sans
═══════════════════════════════════════════════════════════ */

const TB_CSS = `
.tb-wrap {
  font-family: 'DM Sans', sans-serif;
  background: var(--cream);
  height: calc(100vh - 58px);
  display: grid;
  grid-template-columns: 220px 1fr 300px;
  grid-template-rows: 1fr;
  overflow: hidden;
}

/* ── LEFT PANEL ── */
.tb-left {
  background: var(--white);
  border-right: 2px solid var(--rule);
  display: flex; flex-direction: column;
  overflow: hidden;
}
.tb-left-header {
  background: var(--teal2);
  padding: 0.9rem 1rem;
  border-bottom: 2px solid var(--mustard);
  flex-shrink: 0;
}
.tb-left-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1rem; letter-spacing: 2.5px; color: var(--white); line-height: 1;
}
.tb-left-sub {
  font-size: 0.6rem; color: rgba(255,255,255,0.55);
  font-weight: 400; margin-top: 0.15rem; letter-spacing: 0.5px;
}

/* Formation picker */
.tb-formation-bar {
  padding: 0.7rem 0.9rem;
  border-bottom: 1px solid var(--rule);
  flex-shrink: 0;
}
.tb-formation-label {
  font-size: 0.58rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--muted); margin-bottom: 0.45rem;
}
.tb-formations {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
}
.tb-form-btn {
  padding: 0.35rem 0.5rem; text-align: center;
  font-family: 'Bebas Neue', sans-serif; font-size: 0.82rem;
  letter-spacing: 1px; color: var(--muted);
  background: var(--cream2); border: 1.5px solid var(--rule);
  cursor: pointer; transition: all 0.18s;
}
.tb-form-btn:hover { border-color: var(--teal); color: var(--teal); }
.tb-form-btn.on {
  background: var(--teal); border-color: var(--teal);
  color: var(--white);
}

/* Player list */
.tb-player-pool {
  flex: 1; overflow-y: auto;
  padding: 0.6rem 0.9rem;
}
.tb-pool-label {
  font-size: 0.58rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--muted); margin-bottom: 0.5rem;
  display: flex; align-items: center; justify-content: space-between;
}
.tb-pool-count {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 0.88rem; letter-spacing: 1px; color: var(--teal);
}
.tb-pool-player {
  display: flex; align-items: center; gap: 0.55rem;
  padding: 0.48rem 0.6rem; margin-bottom: 0.3rem;
  background: var(--cream2); border: 1.5px solid var(--rule);
  cursor: grab; transition: all 0.18s; user-select: none;
}
.tb-pool-player:hover {
  border-color: var(--teal); background: var(--teal4);
}
.tb-pool-player.on-pitch {
  opacity: 0.4; cursor: not-allowed;
}
.tb-pool-av {
  width: 26px; height: 26px; border-radius: 50%;
  background: var(--teal); border: 1.5px solid var(--ink);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.56rem; font-weight: 800; color: var(--white);
  flex-shrink: 0;
}
.tb-pool-info { flex: 1; min-width: 0; }
.tb-pool-name {
  font-size: 0.78rem; font-weight: 700; color: var(--ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tb-pool-pos {
  font-size: 0.56rem; font-weight: 800; padding: 0.08rem 0.35rem;
  letter-spacing: 1.5px; text-transform: uppercase; flex-shrink: 0;
}
.tb-pool-ovr {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 0.9rem; letter-spacing: 1px; color: var(--darkred);
  flex-shrink: 0;
}

/* System presets */
.tb-systems-bar {
  padding: 0.65rem 0.9rem;
  border-top: 1px solid var(--rule); flex-shrink: 0;
}
.tb-systems-label {
  font-size: 0.58rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--muted); margin-bottom: 0.45rem;
}
.tb-system-btn {
  width: 100%; padding: 0.42rem 0.7rem; text-align: left; margin-bottom: 0.3rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 700;
  color: var(--ink2); background: var(--cream2); border: 1.5px solid var(--rule);
  cursor: pointer; transition: all 0.18s; display: flex;
  align-items: center; gap: 0.5rem;
}
.tb-system-btn:hover { border-color: var(--teal); background: var(--teal4); }
.tb-system-btn.active { border-color: var(--mustard); background: var(--mustard3); }
.tb-system-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}

/* ── PITCH AREA ── */
.tb-pitch-wrap {
  display: flex; flex-direction: column; position: relative;
  background: var(--ink);
}

/* Toolbar */
.tb-toolbar {
  background: var(--ink2); border-bottom: 2px solid var(--mustard);
  padding: 0.5rem 1rem; display: flex; align-items: center;
  gap: 0.5rem; flex-wrap: wrap; flex-shrink: 0;
}
.tb-tool-group {
  display: flex; gap: 0.35rem; align-items: center;
  padding-right: 0.7rem; border-right: 1px solid rgba(255,255,255,0.1);
}
.tb-tool-group:last-child { border-right: none; }
.tb-tool-label {
  font-size: 0.55rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: rgba(255,255,255,0.35);
  margin-right: 0.15rem;
}
.tb-tool-btn {
  padding: 0.32rem 0.65rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 800;
  letter-spacing: 1px; text-transform: uppercase;
  background: transparent; border: 1px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.6); cursor: pointer; transition: all 0.18s;
  display: flex; align-items: center; gap: 0.3rem;
}
.tb-tool-btn:hover { border-color: var(--mustard); color: var(--mustard); }
.tb-tool-btn.on { background: var(--teal); border-color: var(--teal); color: var(--white); }
.tb-tool-btn.danger { border-color: rgba(139,42,42,0.5); color: rgba(248,113,113,0.8); }
.tb-tool-btn.danger:hover { background: var(--darkred); border-color: var(--darkred); color: var(--white); }

/* Pitch */
.tb-pitch {
  flex: 1; position: relative; overflow: hidden;
  background: linear-gradient(
    180deg,
    #2d5a32 0%, #357a3d 10%, #2d6035 20%, #357a3d 30%,
    #2d6035 40%, #357a3d 50%, #2d6035 60%,
    #357a3d 70%, #2d6035 80%, #357a3d 90%, #2d5a32 100%
  );
  cursor: crosshair;
}
.tb-pitch.select-mode { cursor: default; }
.tb-pitch.arrow-mode { cursor: crosshair; }

/* Pitch markings SVG */
.tb-pitch-svg {
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none;
}

/* Arrow canvas */
.tb-arrow-canvas {
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 5;
}

/* Player token on pitch */
.tb-token {
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: 10;
  cursor: grab;
  user-select: none;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tb-token:hover { z-index: 50; }
.tb-token:active { cursor: grabbing; z-index: 60; }
.tb-token.selected .tb-token-circle {
  box-shadow: 0 0 0 3px var(--mustard), 0 0 0 5px rgba(212,148,26,0.4);
}

.tb-token-circle {
  width: 46px; height: 46px; border-radius: 50%;
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; border: 2.5px solid rgba(255,255,255,0.6);
  transition: box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
}
.tb-token-circle.team-a { background: var(--teal); }
.tb-token-circle.team-b {
  background: var(--darkred);
  border-color: rgba(255,180,180,0.7);
}
.tb-token-circle.team-c { background: var(--mustard); }

.tb-token-num {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 0.75rem; letter-spacing: 1px; color: var(--white); line-height: 1;
}
.tb-token-pos {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.44rem; font-weight: 800; color: rgba(255,255,255,0.8);
  letter-spacing: 1px; text-transform: uppercase;
}
.tb-token-label {
  position: absolute; top: 50px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.75); color: var(--white);
  font-family: 'DM Sans', sans-serif; font-size: 0.58rem; font-weight: 700;
  padding: 0.15rem 0.45rem; white-space: nowrap;
  border-radius: 2px; backdrop-filter: blur(4px);
  pointer-events: none;
}
.tb-token-role-badge {
  position: absolute; top: -8px; left: 50%; transform: translateX(-50%);
  background: var(--mustard); color: var(--ink);
  font-family: 'DM Sans', sans-serif; font-size: 0.48rem; font-weight: 800;
  padding: 0.08rem 0.35rem; white-space: nowrap;
  letter-spacing: 0.5px; text-transform: uppercase;
  pointer-events: none;
}

/* Role tooltip on hover — side panel */
.tb-role-tooltip {
  position: absolute;
  top: 50%;
  left: calc(100% + 14px);
  transform: translateY(-50%);
  background: rgba(14,10,6,0.97);
  border: 2px solid var(--mustard);
  padding: 0.9rem 1rem;
  width: 268px;
  z-index: 200;
  pointer-events: none;
  box-shadow: 0 8px 40px rgba(0,0,0,0.8);
  backdrop-filter: blur(4px);
}
/* Arrow pointing left → toward the token */
.tb-role-tooltip::before {
  content: '';
  position: absolute;
  top: 50%; right: 100%;
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-right-color: var(--mustard);
}
/* Flip to left side for right-edge tokens */
.tb-role-tooltip.flip-left {
  left: auto;
  right: calc(100% + 14px);
}
.tb-role-tooltip.flip-left::before {
  right: auto;
  left: 100%;
  border-right-color: transparent;
  border-left-color: var(--mustard);
}
.tb-role-tooltip-name {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.1rem; letter-spacing: 2.5px; color: var(--mustard);
  margin-bottom: 0.5rem; line-height: 1;
  border-bottom: 1px solid rgba(255,255,255,0.12);
  padding-bottom: 0.45rem;
}
.tb-role-tooltip-point {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.7rem; color: rgba(255,255,255,0.85);
  font-weight: 400; line-height: 1.6;
  margin-bottom: 0.3rem;
  display: flex; gap: 0.4rem;
}
.tb-role-tooltip-point::before {
  content: '→'; color: var(--teal); font-weight: 800; flex-shrink: 0; margin-top: 0.05rem;
}
.tb-role-tooltip-hint {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.58rem; color: rgba(255,255,255,0.3);
  margin-top: 0.5rem; font-style: italic;
  border-top: 1px solid rgba(255,255,255,0.08);
  padding-top: 0.4rem;
}

/* Cone / cone markers */
.tb-cone {
  position: absolute; transform: translate(-50%,-50%);
  width: 16px; height: 16px; cursor: pointer; z-index: 8;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
}
.tb-cone:hover { transform: translate(-50%,-50%) scale(1.3); }

/* Text annotation */
.tb-annotation {
  position: absolute; transform: translateX(-50%);
  background: rgba(0,0,0,0.7); color: var(--white);
  font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 700;
  padding: 0.2rem 0.6rem; border-radius: 2px; cursor: pointer;
  white-space: nowrap; z-index: 8;
  border: 1px solid rgba(255,255,255,0.2);
}
.tb-annotation:hover { background: rgba(0,0,0,0.9); }

/* Opposition team tokens */
.tb-opp-bar {
  padding: 0.55rem 0.9rem;
  background: rgba(139,42,42,0.15);
  border-top: 1px solid rgba(139,42,42,0.3);
  flex-shrink: 0;
}
.tb-opp-label {
  font-size: 0.55rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--darkred); margin-bottom: 0.35rem;
  display: flex; align-items: center; justify-content: space-between;
}
.tb-opp-toggle {
  padding: 0.25rem 0.65rem; background: transparent;
  border: 1.5px solid var(--darkred); color: var(--darkred);
  font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800;
  letter-spacing: 1px; text-transform: uppercase; cursor: pointer; transition: all 0.18s;
}
.tb-opp-toggle.on { background: var(--darkred); color: var(--white); }
.tb-opp-toggle:hover { background: var(--darkred); color: var(--white); }
.tb-drop-highlight {
  position: absolute; inset: 0;
  background: rgba(46,125,122,0.08);
  border: 3px dashed rgba(46,125,122,0.4);
  pointer-events: none;
  opacity: 0; transition: opacity 0.2s;
}
.tb-drop-highlight.active { opacity: 1; }

/* ── RIGHT PANEL ── */
.tb-right {
  background: var(--white);
  border-left: 2px solid var(--rule);
  display: flex; flex-direction: column;
  overflow: hidden;
}
.tb-right-tabs {
  display: flex; border-bottom: 1.5px solid var(--rule); flex-shrink: 0;
}
.tb-right-tab {
  flex: 1; padding: 0.65rem 0.3rem; text-align: center;
  font-family: 'DM Sans', sans-serif; font-size: 0.62rem; font-weight: 800;
  letter-spacing: 1px; text-transform: uppercase; cursor: pointer;
  color: var(--muted); border-bottom: 2px solid transparent;
  margin-bottom: -1.5px; transition: all 0.18s;
}
.tb-right-tab:hover { color: var(--teal); }
.tb-right-tab.on { color: var(--teal); border-bottom-color: var(--teal); }

.tb-right-content { flex: 1; overflow-y: auto; padding: 0.9rem; }

/* Player detail panel */
.tb-player-detail-header {
  background: var(--teal2); margin: -0.9rem -0.9rem 0.9rem;
  padding: 0.9rem; border-bottom: 2px solid var(--mustard);
}
.tb-player-detail-name {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.2rem; letter-spacing: 2px; color: var(--white); line-height: 1;
}
.tb-player-detail-meta {
  font-size: 0.62rem; color: rgba(255,255,255,0.55);
  font-weight: 300; margin-top: 0.15rem;
}
.tb-player-detail-ovr {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2.2rem; letter-spacing: 2px; color: var(--mustard); line-height: 1;
  text-align: right;
}

/* Role selector */
.tb-role-select {
  width: 100%; background: var(--cream);
  border: 1.5px solid var(--rule); border-bottom: 2px solid var(--teal);
  padding: 0.55rem 0.75rem; color: var(--ink);
  font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600;
  outline: none; cursor: pointer; margin-bottom: 0.8rem;
}

/* Responsibility list */
.tb-resp-item {
  display: flex; align-items: flex-start; gap: 0.5rem;
  padding: 0.42rem 0; border-bottom: 1px solid var(--rule2);
  font-size: 0.76rem; color: var(--ink2); line-height: 1.5;
}
.tb-resp-item::before { content: '→'; color: var(--teal); font-weight: 800; flex-shrink: 0; margin-top: 0.05rem; }

/* Attached drill */
.tb-drill-attached {
  background: var(--teal4); border: 1.5px solid var(--teal);
  border-left: 4px solid var(--teal); padding: 0.6rem 0.75rem;
  margin-bottom: 0.45rem;
}
.tb-drill-title { font-size: 0.8rem; font-weight: 800; color: var(--ink); margin-bottom: 0.18rem; }
.tb-drill-meta-small { font-size: 0.65rem; color: var(--muted); font-style: italic; }

/* Session summary */
.tb-session-card {
  background: var(--cream2); border: 1.5px solid var(--rule);
  border-left: 4px solid var(--teal); padding: 0.75rem 0.85rem;
  margin-bottom: 0.6rem;
}
.tb-session-title { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; letter-spacing: 1.5px; color: var(--ink); margin-bottom: 0.25rem; }
.tb-session-desc { font-size: 0.72rem; color: var(--muted); font-style: italic; font-weight: 300; line-height: 1.6; }

/* Instruction notes */
.tb-notes-area {
  width: 100%; background: var(--cream);
  border: 1.5px solid var(--rule); border-bottom: 2px solid var(--ink);
  padding: 0.6rem 0.75rem; color: var(--ink);
  font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 400;
  outline: none; resize: vertical; height: 80px;
  font-style: italic; margin-bottom: 0.8rem;
}
.tb-notes-area:focus { border-bottom-color: var(--teal); }

/* Section label */
.tb-sec-label {
  font-size: 0.58rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--teal);
  margin-bottom: 0.45rem; margin-top: 0.7rem; display: block;
}
.tb-sec-label:first-child { margin-top: 0; }

/* Action buttons */
.tb-btn-primary {
  width: 100%; padding: 0.7rem; background: var(--teal);
  color: var(--white); border: 2px solid var(--teal);
  font-family: 'DM Sans', sans-serif; font-weight: 800;
  font-size: 0.72rem; letter-spacing: 2px; text-transform: uppercase;
  cursor: pointer; transition: all 0.2s; margin-bottom: 0.45rem;
}
.tb-btn-primary:hover { background: var(--teal2); border-color: var(--teal2); }
.tb-btn-secondary {
  width: 100%; padding: 0.7rem; background: transparent;
  color: var(--ink); border: 1.5px solid var(--rule);
  font-family: 'DM Sans', sans-serif; font-weight: 700;
  font-size: 0.72rem; letter-spacing: 2px; text-transform: uppercase;
  cursor: pointer; transition: all 0.2s; margin-bottom: 0.45rem;
}
.tb-btn-secondary:hover { border-color: var(--teal); color: var(--teal); }

/* Attrs mini */
.tb-mini-attrs {
  display: grid; grid-template-columns: repeat(3,1fr); gap: 2px; margin: 0.6rem 0;
}
.tb-mini-attr {
  background: var(--cream2); padding: 0.22rem 0.35rem;
  display: flex; justify-content: space-between; align-items: center;
  border: 1px solid var(--rule2);
}
.tb-mini-l { font-size: 0.54rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; }
.tb-mini-v { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; letter-spacing: 1px; }

/* Board summary */
.tb-summary-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.42rem 0; border-bottom: 1px solid var(--rule2);
  font-size: 0.75rem;
}
.tb-summary-label { font-weight: 500; color: var(--muted); }
.tb-summary-val { font-weight: 800; color: var(--ink); }

/* Empty state */
.tb-empty {
  text-align: center; padding: 2rem 1rem;
  font-family: 'DM Sans', sans-serif;
}
.tb-empty-icon { font-size: 2.5rem; margin-bottom: 0.8rem; }
.tb-empty-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.2rem; letter-spacing: 2px; margin-bottom: 0.4rem; }
.tb-empty-sub { font-size: 0.78rem; color: var(--muted); font-style: italic; font-weight: 300; line-height: 1.6; }

/* Responsive */
@media(max-width: 1100px) {
  .tb-wrap { grid-template-columns: 200px 1fr 220px; }
}
@media(max-width: 900px) {
  .tb-wrap { grid-template-columns: 1fr; grid-template-rows: auto 1fr auto; height: auto; overflow:auto; }
  .tb-left { max-height: 260px; border-right:none; border-bottom:2px solid var(--rule); overflow-y:auto; }
  .tb-right { max-height: 320px; border-left:none; border-top:2px solid var(--rule); }
  .tb-pitch { height: min(350px, 55vw); min-height:240px; }
  .tb-toolbar { flex-wrap:wrap; padding:0.4rem 0.6rem; gap:0.3rem; }
}
`;

/* ══════════════════════════════════
   FORMATION LAYOUTS
   Positions as % of pitch width/height
══════════════════════════════════ */
const FORMATIONS = {
  "4-3-3": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"CM",x:70,y:53,num:8},{pos:"CM",x:50,y:50,num:4},{pos:"CM",x:30,y:53,num:6},
    {pos:"RW",x:80,y:28,num:7},{pos:"ST",x:50,y:20,num:9},{pos:"LW",x:20,y:28,num:11},
  ],
  "4-4-2": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"RM",x:82,y:52,num:7},{pos:"CM",x:62,y:52,num:8},{pos:"CM",x:38,y:52,num:4},{pos:"LM",x:18,y:52,num:11},
    {pos:"ST",x:62,y:22,num:9},{pos:"ST",x:38,y:22,num:10},
  ],
  "4-4-2 Diamond": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"CDM",x:50,y:60,num:4},
    {pos:"CM",x:72,y:46,num:8},{pos:"CM",x:28,y:46,num:6},
    {pos:"CAM",x:50,y:34,num:10},
    {pos:"ST",x:62,y:20,num:9},{pos:"ST",x:38,y:20,num:11},
  ],
  "4-2-3-1": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"CDM",x:62,y:58,num:4},{pos:"CDM",x:38,y:58,num:8},
    {pos:"RW",x:80,y:36,num:7},{pos:"CAM",x:50,y:34,num:10},{pos:"LW",x:20,y:36,num:11},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "4-1-4-1": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:73,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:73,num:3},
    {pos:"CDM",x:50,y:60,num:4},
    {pos:"RM",x:85,y:45,num:7},{pos:"CM",x:65,y:45,num:8},{pos:"CM",x:35,y:45,num:6},{pos:"LM",x:15,y:45,num:11},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "4-3-2-1": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"CM",x:68,y:56,num:8},{pos:"CDM",x:50,y:60,num:4},{pos:"CM",x:32,y:56,num:6},
    {pos:"CAM",x:63,y:36,num:10},{pos:"CAM",x:37,y:36,num:11},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "3-4-3": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:76,num:5},{pos:"CB",x:50,y:78,num:4},{pos:"CB",x:72,y:76,num:6},
    {pos:"RWB",x:85,y:53,num:2},{pos:"CM",x:63,y:53,num:8},{pos:"CM",x:37,y:53,num:6},{pos:"LWB",x:15,y:53,num:3},
    {pos:"RW",x:80,y:25,num:7},{pos:"ST",x:50,y:18,num:9},{pos:"LW",x:20,y:25,num:11},
  ],
  "3-5-2": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:76,num:5},{pos:"CB",x:50,y:78,num:4},{pos:"CB",x:72,y:76,num:6},
    {pos:"RWB",x:88,y:50,num:2},{pos:"CM",x:67,y:52,num:8},{pos:"CM",x:50,y:50,num:4},{pos:"CM",x:33,y:52,num:6},{pos:"LWB",x:12,y:50,num:3},
    {pos:"ST",x:62,y:20,num:9},{pos:"ST",x:38,y:20,num:10},
  ],
  "3-5-2 (Low Block)": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:80,num:5},{pos:"CB",x:50,y:82,num:4},{pos:"CB",x:72,y:80,num:6},
    {pos:"RWB",x:88,y:65,num:2},{pos:"CM",x:67,y:62,num:8},{pos:"CDM",x:50,y:65,num:4},{pos:"CM",x:33,y:62,num:6},{pos:"LWB",x:12,y:65,num:3},
    {pos:"ST",x:62,y:28,num:9},{pos:"ST",x:38,y:28,num:10},
  ],
  "3-4-1-2": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:76,num:5},{pos:"CB",x:50,y:79,num:4},{pos:"CB",x:72,y:76,num:6},
    {pos:"RWB",x:87,y:54,num:2},{pos:"CM",x:64,y:55,num:8},{pos:"CM",x:36,y:55,num:6},{pos:"LWB",x:13,y:54,num:3},
    {pos:"CAM",x:50,y:36,num:10},
    {pos:"ST",x:63,y:20,num:9},{pos:"ST",x:37,y:20,num:11},
  ],
  "5-3-2": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"RWB",x:88,y:65,num:2},{pos:"CB",x:70,y:76,num:5},{pos:"CB",x:50,y:79,num:4},{pos:"CB",x:30,y:76,num:6},{pos:"LWB",x:12,y:65,num:3},
    {pos:"CM",x:68,y:50,num:8},{pos:"CM",x:50,y:48,num:4},{pos:"CM",x:32,y:50,num:6},
    {pos:"ST",x:62,y:20,num:9},{pos:"ST",x:38,y:20,num:10},
  ],
  "5-4-1": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"RWB",x:87,y:67,num:2},{pos:"CB",x:70,y:76,num:5},{pos:"CB",x:50,y:79,num:4},{pos:"CB",x:30,y:76,num:6},{pos:"LWB",x:13,y:67,num:3},
    {pos:"RM",x:82,y:52,num:7},{pos:"CM",x:62,y:52,num:8},{pos:"CM",x:38,y:52,num:6},{pos:"LM",x:18,y:52,num:11},
    {pos:"ST",x:50,y:20,num:9},
  ],
  "4-3-1-2": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:72,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:72,num:3},
    {pos:"CM",x:68,y:56,num:8},{pos:"CDM",x:50,y:60,num:4},{pos:"CM",x:32,y:56,num:6},
    {pos:"CAM",x:50,y:36,num:10},
    {pos:"ST",x:63,y:20,num:9},{pos:"ST",x:37,y:20,num:11},
  ],
  "4-5-1": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:73,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:73,num:3},
    {pos:"RM",x:86,y:52,num:7},{pos:"CM",x:67,y:52,num:8},{pos:"CM",x:50,y:55,num:4},{pos:"CM",x:33,y:52,num:6},{pos:"LM",x:14,y:52,num:11},
    {pos:"ST",x:50,y:20,num:9},
  ],
  "4-4-1-1": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:73,num:2},{pos:"CB",x:62,y:76,num:5},
    {pos:"CB",x:38,y:76,num:6},{pos:"LB",x:20,y:73,num:3},
    {pos:"RM",x:83,y:53,num:7},{pos:"CM",x:63,y:53,num:8},{pos:"CM",x:37,y:53,num:4},{pos:"LM",x:17,y:53,num:11},
    {pos:"CAM",x:50,y:34,num:10},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "3-6-1": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:76,num:5},{pos:"CB",x:50,y:79,num:4},{pos:"CB",x:72,y:76,num:6},
    {pos:"RM",x:88,y:53,num:2},{pos:"CM",x:70,y:53,num:8},{pos:"CM",x:55,y:50,num:10},{pos:"CM",x:45,y:50,num:6},{pos:"CM",x:30,y:53,num:7},{pos:"LM",x:12,y:53,num:3},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "4-3-3 Possession": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:78,y:70,num:2},{pos:"CB",x:61,y:75,num:5},
    {pos:"CB",x:39,y:75,num:6},{pos:"LB",x:22,y:70,num:3},
    {pos:"CDM",x:65,y:57,num:8},{pos:"CDM",x:50,y:62,num:4},{pos:"CDM",x:35,y:57,num:6},
    {pos:"RW",x:82,y:30,num:7},{pos:"ST",x:50,y:18,num:9},{pos:"LW",x:18,y:30,num:11},
  ],
  "3-3-3-1": [
    {pos:"GK",x:50,y:88,num:1},
    {pos:"CB",x:28,y:76,num:5},{pos:"CB",x:50,y:79,num:4},{pos:"CB",x:72,y:76,num:6},
    {pos:"CM",x:25,y:60,num:8},{pos:"CDM",x:50,y:62,num:4},{pos:"CM",x:75,y:60,num:6},
    {pos:"RW",x:75,y:38,num:7},{pos:"CAM",x:50,y:36,num:10},{pos:"LW",x:25,y:38,num:11},
    {pos:"ST",x:50,y:18,num:9},
  ],
  "4-3-3 (High Press)": [
    {pos:"GK",x:50,y:88,num:1},{pos:"RB",x:80,y:70,num:2},{pos:"CB",x:62,y:74,num:5},
    {pos:"CB",x:38,y:74,num:6},{pos:"LB",x:20,y:70,num:3},
    {pos:"CM",x:68,y:50,num:8},{pos:"CDM",x:50,y:55,num:4},{pos:"CM",x:32,y:50,num:6},
    {pos:"RW",x:82,y:26,num:7},{pos:"ST",x:50,y:18,num:9},{pos:"LW",x:18,y:26,num:11},
  ],
};

/* ══════════════════════════════
   ROLE RESPONSIBILITIES DATA
══════════════════════════════ */
const ROLE_DATA = {
  "Sweeper Keeper":["Sweep aggressively behind the defensive line — be the 11th outfield player","Participate in build-up from the back as a natural outfield option","Distribute short to CBs under pressure — never hoof aimlessly","Claim crosses early and with authority — command the box","Organise the defensive line constantly — be the eyes of the team"],
  "Shot-stopper":["Position on the goal line — do not over-commit or rush out","React rather than anticipate — stay big and make yourself large","Deal with close-range shots and 1v1 situations decisively","Command the 6-yard box on corners and crosses","Organise the defensive line from behind — constant communication"],
  "Ball-playing CB":["Receive from the GK under press and play forward quickly","Carry the ball into midfield when the press is beaten — be brave","Switch play accurately across the defensive line under pressure","Step out to intercept when the trigger is correct — always have cover","Lead defensive line communication — who steps, who holds"],
  "Defensive CB":["Stay goalside at all times — never be beaten in behind","Win every aerial duel in and around the defensive box","Communicate with your partner constantly — own your zone","Never gamble — let the ball run across when in doubt","Deal with physical strikers using your body — hold them off"],
  "Aerial CB":["Dominate all aerial duels in both penalty boxes","Lead the defensive line organisation at set pieces","Win the first ball on attacking set pieces — the dangerous flick","Hold your position in the box — do not be dragged out of shape","Communicate the defensive line height — push up as a unit"],
  "Attacking Full-back":["Overlap the winger when they go inside — arrive with pace","Deliver from the byline — driven crosses, floated balls and cutbacks","Create 2v1 with the winger — be the spare man in wide areas","Recover immediately when possession is lost — sprint is non-negotiable","Time the overlap — too early and you are marked, too late and it's gone"],
  "Defensive Full-back":["Prioritise defensive positioning over attacking ambition","Track the opposition winger tightly in all zones of the pitch","Only overlap when there is clear defensive cover behind you","Win the 1v1 duel — do not allow the cross — stay on your feet","Cover the channel when the CB steps out to press"],
  "Inverted Full-back":["Tuck inside into the CDM position when in possession — create the overload","Allow the winger to occupy the wide space — do not compete with them","Create 3v2 in central midfield — you are the unexpected extra man","Cover the defensive line when tucked — communicate with the CB","Drive forward with the ball when the press is beaten — you are an attacker"],
  "Wing-back":["Provide the only width in the system — hug the touchline in transition","Attack with quality — deliver or cut inside and shoot when on your stronger foot","Sprint the full length of the pitch repeatedly — you cover 70+ metres each switch","Combine with the No.10 or second striker in the half-space","Drop into the defensive five when possession is lost — discipline first"],
  "Holding Pivot":["Screen the back four — never leave a gap in front of the CBs","Receive from the CBs under pressure and recycle possession quickly","Cover for advancing full-backs — know when to drop and fill","Only press when the trigger is absolutely correct — hold position otherwise","Dictate the tempo of the game from deep — control the rhythm"],
  "Deep Pivot":["Sit deep and protect the back four — defensive discipline above creativity","Intercept and break up play — you are the defensive midfielder","Distribute simply — keep it and build — no unnecessary risks","Track runners from midfield through your zone","Be the anchor the rest of the team can rely on in transition"],
  "Box-to-Box CM":["Cover the full length of the pitch in every attack and every defensive phase","Arrive late in the box from deep — time the run to arrive unmarked","Win the second ball at set pieces — be first to every loose ball","Press from the front when possession is lost — set the team's defensive shape","Drive the team forward with energy, leadership and relentless running"],
  "Creative CM":["Find pockets of space between the lines to receive — show for the ball constantly","Play the final pass — the through-ball, the key pass, the disguised delivery","Play in one or two touches in tight areas — do not hold the ball","Press intelligently — position yourself for the counter rather than chasing","Set pieces — deliver corners and free kicks with quality and variation"],
  "Press Trigger CM":["Be the trigger — your press activates the whole team's defensive shape","Win the ball back within 5 seconds of losing it — the counter-press","Cover for the CDM when they step — you become the pivot","Maintain your defensive intensity for 90 minutes — never drop","Win the midfield battle — the game is won or lost in your zone"],
  "Ball-Winning CM":["Attack the ball when it arrives in your zone — do not wait","Win physical duels in midfield — use your body and your desire","Make the tackle early — do not let the player turn","Transition immediately — win it and give it simply","Protect the team — accept the yellow card if the situation demands it"],
  "Deep-Lying Playmaker":["Receive between the lines and under pressure — be comfortable on the ball","Pick the pass that others cannot see — the diagonals and the switches","Control the tempo — slow it down when needed, accelerate when the chance is there","Never lose the ball cheaply — you are the conductor","Drop between the CBs in build-up to create the 3v2 against the press"],
  "CAM / No.10":["Receive between the lines — check away then check back to create the space","Play the through-ball when the defensive line steps up — timing is everything","Combine with the striker in one-two and third-man situations","Press the first defender — you start the team's defensive shape from the front","Score goals — arrive late in the box and be the one who arrives unmarked"],
  "Shadow Striker":["Play between the lines — link midfield and attack but never drop too deep","Score goals — you are a goalscorer who starts deep","Press the defensive line aggressively when the ball goes to the CB","Combine with the striker — the give-and-go, the overlap run in behind","Make late runs into the box — time your arrival to stay onside"],
  "Inverted Winger":["Receive on the wrong wing — your stronger foot is your weapon","Drive inside at pace — the cut inside is your primary action","Shoot on sight — the diagonal shot into the far corner","Support the overlapping full-back behind you — give and go outside","Press the opposition full-back aggressively when they have the ball"],
  "Wide Forward":["Stay wide — you provide the width the system depends on","Take on the full-back 1v1 — beat them and deliver the cross","Deliver from the byline — driven crosses, cutbacks and floated balls","Press the CB when the opposition full-back has the ball — angle the run","Make the run in behind when the striker drops deep — exploit the space"],
  "Attacking Midfielder":["Float between the lines — find space where the opponent cannot mark you","Be the creative spark — dribble into space, play the key pass, shoot from range","Lead the press — your positioning traps the opponent's build-up","Combine with the strikers in tight areas — quick combinations in the final third","Arrive in the box late — the run from deep that the defence cannot pick up"],
  "Clinical Finisher":["Get into the box — you are a finisher, not a creator or a ball carrier","Demand the ball in your shooting positions — penalty spot, six yard box","Finish with one touch whenever possible — the first touch finish","Movement in behind the defensive line — near post, far post, and the diagonal","Set the press — your movement activates the team's defensive shape"],
  "Target Man":["Hold the ball up under physical pressure — you bring others into the game","Lay off and link — the flick, the chest, the hold and turn","Win aerial duels from long balls — your presence changes the game","Set piece presence — near post flick, far post header, penalty spot","Make the defensive life hard — outmuscle, outjump, outlast your marker"],
  "False 9":["Drop deep to receive between the lines — drag the CBs with you","Create space in behind for the midfield runners — your movement is the pass","Combine in tight areas with the No.10 and wingers — third-man combinations","Score goals — despite the deep position, arrive in the box late and score","Demand the ball, turn and drive — the dribble from deep is your weapon"],
  "Pressing Striker":["Set the press — your movement and angle activates the whole team's press","Win the ball high — the best chance creation comes from the counter-press","Press the CB with the correct angle — cut off the pass to the full-back","Hold the ball when it arrives from the press — keep it and let the team build","Sprint in behind — the direct run that exploits the gap the press creates"],
  "Second Striker":["Play off the main striker — find the pockets they leave","Score goals — your role is to arrive late and finish","Create for the striker — the clever lay-off, the through-ball from deep","Press the defensive line — force errors in the opposition build-up","Be the link — between midfield and attack, between wide and central"],
};

/* ══════════════════════════════
   DRILL SUGGESTIONS BY ROLE
══════════════════════════════ */
const ROLE_DRILLS = {
  "Sweeper Keeper":["Sweeping behind the line — timed race with striker","Short pass under press (GK + CB + presser)","Distribution patterns — 4 targets, 4 choices, time pressure"],
  "Shot-stopper":["Reaction saves — rapid fire from 8 yards","1v1 composure drill — goalkeeper vs striker breakaway","Cross claiming — high balls into congested area"],
  "Ball-playing CB":["3-back build-up vs 3-player press","Carrying from deep — trigger and stop","Switch of play across the line — accuracy and weight"],
  "Defensive CB":["Defensive header repetitions — 15 each side","1v1 defending — hold position and force wide","Cover shadow — partner steps, you hold"],
  "Aerial CB":["Aerial duel heading — 10 repetitions each side from crosses","Set piece defensive organisation — zonal marking rehearsal","Win the first ball — attacking corner routine"],
  "Attacking Full-back":["Overlap timing with the winger — 2v1 creation","Byline delivery — driven, floated and cut-back","Sprint recovery after the overlap — 60m sprint back"],
  "Defensive Full-back":["1v1 vs pacey winger — defensive positioning","Tracking run — follow the runner through 3 zones","Channel coverage — CB steps, you cover the space"],
  "Inverted Full-back":["Tuck-in rotation with the CDM — shadow play","Progressive carry from the right back zone into midfield","Covering the defensive line after tucking — GK + CB coordination"],
  "Wing-back":["Wing-back attack and recover — full length of the pitch","Cross delivery from the byline — driven, floated, cutback","1v1 defensive duel — forced wide, no foul"],
  "Holding Pivot":["Rondo press resistance — pivot under pressure in the middle","Cover shadow — cut the passing lane into the striker","Tempo drill — receive, control, distribute — slow then accelerate"],
  "Deep Pivot":["Intercept and recycle — read the pass and step to win it","Sit behind the press — hold position as others press","Simple distribution under pressure — 3 options always"],
  "Box-to-Box CM":["End-to-end stamina drill — 8 reps full pitch","Late box arrival — timing off the striker's drop","Second ball competition — two players, one ball, high intensity"],
  "Creative CM":["Through-ball timing — the run triggers the pass","Tight area combination play — one and two touch in a rondo","Set piece delivery — corner and free kick accuracy drill"],
  "Press Trigger CM":["Press trigger activation — angle, intensity, timing","Recovery sprint — press then drop 20 metres","High press 2v1 — CM + striker pressing the back two"],
  "Ball-Winning CM":["Physical midfield duel — ball in the air, two players","Tackle timing — approach, jockey, commit","Win and recycle — tackle then give a 5-yard pass immediately"],
  "Deep-Lying Playmaker":["Receive under pressure and switch — 3v1 rondo as pivot","Progressive diagonal — pass that breaks the defensive line","Build-up from between the CBs — drop deep, receive, drive forward"],
  "CAM / No.10":["Receive between the lines — check and turn","Through-ball timing — run triggers the pass, not the other way","Press recovery — No.10 as the first defender"],
  "Shadow Striker":["Third-man run — striker drops, shadow striker arrives","Press the defensive line — force the error","Late run timing — stay onside off the last defender"],
  "Inverted Winger":["Cut inside and shoot — 3 finishing angles in 8 minutes","1v1 isolation — step-over, inside cut, finish","Combine with the overlapping full-back — give and go outside"],
  "Wide Forward":["1v1 pace duel with the full-back — beat and deliver","Pressing the full-back — angle of approach, force back","Crossing quality — driven, floated, cut-back into the area"],
  "Attacking Midfielder":["Half-space combination — AM + winger in 2v1","Shoot from range — 8 shots per session from outside the area","Press from the front — AM traps the pass to the CB"],
  "Clinical Finisher":["Finishing under pressure — 1 second to shoot from cross","Movement in behind — near post and far post run timing","One-touch finishing circuit — 6 angles, 8 reps each"],
  "Target Man":["Hold-up under physical pressure — back to goal, 4 defenders","Aerial duel heading — 10 reps each side from varied deliveries","Layoff and spin — link play combination, target + runner"],
  "False 9":["Drop and drive — receive deep then carry forward at pace","1v1 in the half-space — dribble into the shooting area","Third-man combination — false 9 as the connector between lines"],
  "Pressing Striker":["Press trigger activation — angle and intensity from the front","Recovery sprint — high press then drop 25 metres","Counter-press — win it back within 5 seconds of losing it"],
  "Second Striker":["Arrive late — second striker times the run off the target man","Link play combination — hold and lay off for the arriving runner","Press the defensive line 2v2 with the striker"],
};

/* ══════════════════════════════
   SYSTEM PRESETS
══════════════════════════════ */
const SYSTEM_PRESETS = [

  /* ══ 4-3-3 — 9 systems ══ */
  {name:"Guardiola 4-3-3",      category:"Possession",     color:"#5bc5f5", formation:"4-3-3",
   description:"Positional play. Inverted FBs create midfield overload. False 9 drags CBs. GK as outfield player.",
   principles:["Occupy all 5 lanes simultaneously","Invert full-backs into midfield","GK initiates build-up","False 9 creates space for CM runners","Win ball back within 5 seconds"],
   roles:{"GK":"Sweeper Keeper","RB":"Inverted Full-back","CB":"Ball-playing CB","LB":"Inverted Full-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","RW":"Inverted Winger","LW":"Inverted Winger","ST":"False 9"}},

  {name:"Klopp 4-3-3",          category:"High Press",     color:"#c8102e", formation:"4-3-3",
   description:"Gegenpressing. Win it back within 6 seconds. Vertical transitions. Front three press the defensive line relentlessly.",
   principles:["Win ball back in 6 seconds","Vertical direct transitions","Full-backs attack aggressively","CM is press trigger","Front three press as a unit"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Defensive CB","LB":"Attacking Full-back","CM":"Box-to-Box CM","CDM":"Press Trigger CM","RW":"Wide Forward","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Arteta 4-3-3",         category:"High Press",     color:"#EF0107", formation:"4-3-3",
   description:"Structured possession with aggressive width. One inverted FB, one attacking FB. No.10 triggers press. Build from back bravely.",
   principles:["One FB inverts to create midfield 3v2","Inverted winger + wide forward split","No.10 triggers press from inside","Compact mid-block that presses on trigger","Build from the back under pressure"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Inverted Full-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","CAM":"CAM / No.10","RW":"Inverted Winger","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Ten Hag 4-3-3",        category:"High Press",     color:"#DA291C", formation:"4-3-3",
   description:"Organised positional press. Wingers press CBs when ball goes wide. Full-backs prevent switches. CMs cover the second ball.",
   principles:["Wingers press the CB when ball goes wide","Full-backs push high to prevent switches","CM covers second ball after press","Organised press — a trap, not a chase","Win high and attack vertically"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Press Trigger CM","CDM":"Holding Pivot","RW":"Inverted Winger","LW":"Wide Forward","ST":"Pressing Striker"}},

  {name:"Ancelotti 4-3-3",      category:"Counter-attack", color:"#000000", formation:"4-3-3",
   description:"Individual freedom within structure. World-class talent in key moments. Wide wingers attack 1v1. One CM arrives late in box.",
   principles:["Trust individual quality in key moments","Wide wingers attack 1v1 directly","One CM arrives late in the box","Defensive stability comes first","Win it and find the winger immediately"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","RW":"Inverted Winger","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Cruyff 4-3-3",         category:"Possession",     color:"#AE0025", formation:"4-3-3",
   description:"Total football. Every player can occupy every position. Width, movement, positional rotation. The original 4-3-3 possession template.",
   principles:["Every player comfortable in multiple positions","Width is fundamental — stretch the opposition","Constant positional rotation","Short passes and triangles","Press immediately after losing the ball"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Creative CM","CDM":"Holding Pivot","RW":"Wide Forward","LW":"Wide Forward","ST":"False 9"}},

  {name:"De Zerbi 4-3-3",       category:"Possession",     color:"#005daa", formation:"4-3-3",
   description:"Radical possession play. GK and CBs create 3v2 in build-up. Play out from every situation. Progressive passing through the lines.",
   principles:["GK + CBs create numerical superiority in build-up","Play through pressure — never bypass the press","CMs move constantly between the lines","Wingers invert to combine in central zones","High defensive line — trust the GK to sweep"],
   roles:{"GK":"Sweeper Keeper","RB":"Inverted Full-back","CB":"Ball-playing CB","LB":"Inverted Full-back","CM":"Creative CM","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","LW":"Inverted Winger","ST":"False 9"}},

  {name:"Wenger 4-3-3",         category:"Possession",     color:"#EF0107", formation:"4-3-3",
   description:"Beautiful attacking football. Technical quality, movement and pace. Wingers take on defenders 1v1. Quick combinations in the final third.",
   principles:["Technical quality in every position","Wingers attack 1v1 — pace and trickery","Attacking full-backs create wide overloads","Quick one and two-touch in the final third","Press intelligently to win the ball high"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","CAM":"CAM / No.10","RW":"Wide Forward","LW":"Inverted Winger","ST":"Clinical Finisher"}},

  {name:"Direct 4-3-3",         category:"Direct",         color:"#d4941a", formation:"4-3-3",
   description:"Direct football with wide runners. Find the striker quickly, use width, attack in behind. CM trio presses and covers. No overcomplication.",
   principles:["Find the striker early — direct ball in behind","Wingers attack the line with pace","CM trio — two box-to-box, one sits","Press on transitions — win it back quickly","Keep it simple — direct football works"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","RW":"Wide Forward","LW":"Wide Forward","ST":"Target Man"}},

  /* ══ 4-2-3-1 — 8 systems ══ */
  {name:"Pochettino 4-2-3-1",   category:"High Press",     color:"#132257", formation:"4-2-3-1",
   description:"Aggressive high press with double pivot providing cover. Front four press as a unit. No.10 triggers by cutting the CM passing lane.",
   principles:["Front four press as a coordinated unit","No.10 triggers by cutting the CM passing lane","Double pivot covers the press","Full-backs push high to prevent switches","Win in the middle third and attack immediately"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Defensive CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RW":"Wide Forward","CAM":"CAM / No.10","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Mourinho 4-2-3-1",     category:"Counter-attack", color:"#0047AB", formation:"4-2-3-1",
   description:"Deep block, clinical counter. Absorb pressure in compact 4-2-3-1. Find No.10 quickly, attack in behind with pace. Striker movement is key.",
   principles:["Compact mid-block — two banks of four","Double pivot never exposes the back four","No.10 links transitions — find the striker fast","Striker movement creates counter space","Full-backs stay defensive — wingers and No.10 attack"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","RW":"Wide Forward","CAM":"CAM / No.10","LW":"Inverted Winger","ST":"Clinical Finisher"}},

  {name:"Ferguson 4-2-3-1",     category:"Counter-attack", color:"#DA291C", formation:"4-2-3-1",
   description:"Direct with width and pace. Wide players take on full-backs 1v1. No.10 links play. Counter-attack is a primary weapon. Set pieces exploited.",
   principles:["Wide players attack full-backs 1v1 — pace is the weapon","No.10 links play between midfield and attack","Counter-attack in 3v3 or 4v3 — ruthlessly direct","Set pieces are a genuine goal threat","Defensive shape is the foundation"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Aerial CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RW":"Wide Forward","CAM":"CAM / No.10","LW":"Inverted Winger","ST":"Target Man"}},

  {name:"Nagelsmann 4-2-3-1",   category:"Possession",     color:"#e63946", formation:"4-2-3-1",
   description:"Positional pressing hybrid. High defensive line, aggressive press triggers, possession-based build-up. Versatile roles in midfield.",
   principles:["High defensive line — trust the GK","Aggressive press triggers from the No.10","Double pivot: one holds, one drives forward","Build from the back through the pivot","Versatile CMs — press and create in equal measure"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","CAM":"Attacking Midfielder","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Emery 4-2-3-1",        category:"Possession",     color:"#7FC5E3", formation:"4-2-3-1",
   description:"Disciplined organisation with set piece mastery. Compact shape, press on trigger, exploit dead balls. Structured build-up through double pivot.",
   principles:["Compact defensive organisation — hard to break","Set pieces are a major weapon","Double pivot dictates tempo","Press on defined trigger — not before","Exploit transitions with intelligent running"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RW":"Inverted Winger","CAM":"CAM / No.10","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Enrique 4-2-3-1",      category:"Possession",     color:"#AA151B", formation:"4-2-3-1",
   description:"High possession and high press. Full-backs push to wing-back positions. No.10 between the lines. Technical quality in every position.",
   principles:["Full-backs push high to wing-back positions","No.10 receives between the lines — the key link","Press high as 4-1-4-1 shape out of possession","Switch play quickly to exploit defensive width","Technical quality on the ball — non-negotiable"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","CAM":"Shadow Striker","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Tuchel 4-2-3-1",       category:"High Press",     color:"#034694", formation:"4-2-3-1",
   description:"Fluid pressing system. Players rotate positions. No.10 drops to press or arrives in box. Full-backs invert. Versatility essential.",
   principles:["Players rotate positions fluidly","No.10 drops deep to press or arrives late in box","Full-backs invert to create midfield overload","Win the ball through coordinated press triggers","Attack from wide positions — wingers in 1v1"],
   roles:{"GK":"Sweeper Keeper","RB":"Inverted Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RW":"Wide Forward","CAM":"CAM / No.10","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Ancelotti 4-2-3-1",    category:"Possession",     color:"#FFFFFF", formation:"4-2-3-1",
   description:"Creative freedom around a solid double pivot. Playmaker between the lines. Wide players deliver. Striker and playmaker combine.",
   principles:["Double pivot provides security for creative players","Playmaker operates freely between the lines","Wide players deliver — crosses, cutbacks, combinations","Striker links play and scores","Trust your best players to decide in key moments"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Defensive CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RW":"Inverted Winger","CAM":"Deep-Lying Playmaker","LW":"Wide Forward","ST":"Clinical Finisher"}},

  /* ══ 4-4-2 — 8 systems ══ */
  {name:"Simeone 4-4-2",        category:"Low Block",      color:"#CB3524", formation:"4-4-2",
   description:"The low block. Compact two banks of four. Win aerial duels, exploit set pieces, fast breaks. Two strikers press high to force long balls.",
   principles:["Two compact banks of four — hard to break","Force long balls by pressing with the strikers","Win aerial duels — set pieces are a major threat","Fast break — win the ball and attack with quality","Win 1v1 duels across the pitch — the foundation"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CM":"Press Trigger CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Target Man"}},

  {name:"Bielsa 4-4-2",         category:"High Press",     color:"#FFFFFF", formation:"4-4-2",
   description:"Man-marking press. Every player marks an opponent anywhere on the pitch. Constant intensity from first to last minute.",
   principles:["Every player marks a specific opponent","Press is immediate — no passive defending","Two strikers press the two CBs simultaneously","Full-backs press the opposition full-backs","Win the ball in the opponent's half and attack"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Press Trigger CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Pressing Striker"}},

  {name:"Ferguson 4-4-2",       category:"Counter-attack", color:"#DA291C", formation:"4-4-2",
   description:"Direct counter-attacking 4-4-2. Wide players get up and down. Two strikers work as a pair. Pace on the counter is the weapon.",
   principles:["Wide players provide width and track back hard","Two strikers work in partnership — hold, link, run","Counter-attack with pace — wide players sprint in behind","Set pieces exploited on both sides of the ball","Win your individual battles"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CM":"Box-to-Box CM","RM":"Wide Forward","LM":"Inverted Winger","ST":"Target Man"}},

  {name:"Grassroots 4-4-2",     category:"Direct",         color:"#16a34a", formation:"4-4-2",
   description:"Simple, effective, hard to play against. Two strikers work hard, wide midfielders get up and down. Compact shape, win your battles, play simply.",
   principles:["Simple shape — everyone understands their job","Work hard for each other — no passengers","Win your 1v1 duels across the pitch","Move the ball quickly — don't overhold it","Set pieces are a real weapon — practice every session"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CM":"Box-to-Box CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Target Man"}},

  {name:"Mourinho 4-4-2",       category:"Low Block",      color:"#6CABDD", formation:"4-4-2",
   description:"Defensive mid-block 4-4-2. Compact, physical, organised. Win the second ball in midfield. Counter quickly through the strikers.",
   principles:["Mid-block — invite the opposition then hit on the counter","Physical midfield battles — win the second ball","Two strikers press to prevent comfortable build-up","Wide midfielders track back and defend their zones","Counter directly — find the striker in behind immediately"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CM":"Ball-Winning CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Wenger 4-4-2",         category:"Possession",     color:"#EF0107", formation:"4-4-2",
   description:"Early Wenger possession 4-4-2. Fluid movement, technical quality, attacking full-backs. Two strikers combine — one drops, one runs in behind.",
   principles:["Technical quality on the ball in every position","Attacking full-backs overlap to create wide overloads","Two strikers work in combination — one drops, one attacks","Quick one and two-touch passing in the final third","Press intelligently to win the ball high up the pitch"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Creative CM","RM":"Wide Forward","LM":"Inverted Winger","ST":"Clinical Finisher"}},

  {name:"Ranieri 4-4-2",        category:"Counter-attack", color:"#003090", formation:"4-4-2",
   description:"Compact counter-attacking 4-4-2. Extremely tight defensive shape. One striker holds, one runs. Precise set pieces. Exploit the opposition's fatigue.",
   principles:["Extremely compact defensive shape — no gaps","One striker holds the ball under pressure","One striker makes the run in behind on the counter","Precise set pieces — attacking and defending","Win your duels — the team's defensive record is everything"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CM":"Holding Pivot","RM":"Wide Forward","LM":"Wide Forward","ST":"Target Man"}},

  {name:"Clough 4-4-2",         category:"Direct",         color:"#FFFFFF", formation:"4-4-2",
   description:"Classic direct 4-4-2. Win the ball, use it simply, get forward quickly. The striker partnership is the focal point. Hard to beat on a good day.",
   principles:["Win the first ball — physical presence in midfield","Use the ball simply — no unnecessary risks","Get forward quickly — the striker partnership is the focal point","Work hard without the ball — defend from the front","Set pieces create goals — attack and defend them"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CM":"Box-to-Box CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Target Man"}},

  /* ══ 3-5-2 — 8 systems ══ */
  {name:"Conte 3-5-2",          category:"Counter-attack", color:"#0057A8", formation:"3-5-2",
   description:"Wing-back counter-attack. Compact 3-5-2 in defence, fast transitions through the wing-backs. Two strikers hold and press.",
   principles:["Wing-backs are the game-changers — attack and recover constantly","3-CB provides stability when wing-backs attack","Two strikers press high and work the channels","CMs create overloads in transition","Wing-backs tuck in when out of possession"],
   roles:{"GK":"Shot-stopper","CB":"Defensive CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Ball-Winning CM","ST":"Target Man"}},

  {name:"Mourinho 3-5-2",       category:"Low Block",      color:"#0047AB", formation:"3-5-2",
   description:"Defensive 3-5-2. Five in midfield blocks central areas. Wing-backs track opposition wide players. Two strikers work hard on the counter.",
   principles:["Five in midfield creates numerical superiority in key zones","Wing-backs track the opposition's widemen tightly","Ball-winning CM is the defensive anchor","Compact three-CB line — narrow and hard to penetrate","Counter quickly through the two strikers"],
   roles:{"GK":"Shot-stopper","CB":"Aerial CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Ball-Winning CM","CDM":"Deep Pivot","ST":"Clinical Finisher"}},

  {name:"Alonso 3-5-2",         category:"Possession",     color:"#D4AF37", formation:"3-5-2",
   description:"Possession-dominant 3-5-2. Three CBs build from the back. Wing-backs provide width. Two CMs control the central zones.",
   principles:["Three CBs provide numerical superiority in build-up","Wing-backs push high to create width","Deep CM controls tempo and protects three CBs","Two strikers interchange — one drops, one attacks the line","Press immediately after losing the ball"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Deep-Lying Playmaker","ST":"Clinical Finisher"}},

  {name:"Tuchel 3-5-2",         category:"High Press",     color:"#034694", formation:"3-5-2",
   description:"High pressing 3-5-2. Wing-backs press the opposition full-backs. Three CBs hold a very high defensive line. Two CMs press as a pair.",
   principles:["Wing-backs press the opposition full-backs aggressively","Three CBs hold a very high defensive line","Two CMs press as a pair — coordinated triggers","GK sweeps behind the high line — must be brave","Two strikers press the CBs — force long balls then win them"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Press Trigger CM","CDM":"Holding Pivot","ST":"Pressing Striker"}},

  {name:"Nagelsmann 3-5-2",     category:"Possession",     color:"#e63946", formation:"3-5-2",
   description:"Fluid 3-5-2 that transitions to a 4-3-3 in possession. Wing-backs become wide forwards. Deep playmaker orchestrates from between the CBs.",
   principles:["Shape changes — 3-5-2 in defence, 4-3-3 in possession","Wing-backs become attacking wide forwards in possession","Deep playmaker drops between CBs in build-up","Press in organised waves — not chaotic chasing","Two strikers have licence to rotate positions"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Creative CM","CDM":"Deep-Lying Playmaker","ST":"False 9"}},

  {name:"Sarri 3-5-2",          category:"Possession",     color:"#00A69B", formation:"3-5-2",
   description:"Intense possession play from a 3-5-2. The midfield three dominate central areas. Wing-backs provide the width. Short, quick combinations.",
   principles:["Short passes in tight areas — possession is never given away","Midfield three dominates the centre","Wing-backs provide the width and deliver quality balls","High defensive line — press immediately after losing the ball","Two strikers press and combine — one target, one mobile"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Creative CM","CDM":"Deep-Lying Playmaker","ST":"Pressing Striker"}},

  {name:"Rangnick 3-5-2",       category:"High Press",     color:"#E32221", formation:"3-5-2",
   description:"Intense pressing 3-5-2. High intensity, high defensive line, coordinated press. Win the ball high and use the wing-backs to attack immediately.",
   principles:["Press with maximum intensity — win the ball in the opponent's half","Wing-backs are the primary attacking outlet on winning possession","Three CBs hold a high line and deal with the space","Midfield presses in coordinated waves — everyone works","Transitions are fast — direct ball to wing-back or striker"],
   roles:{"GK":"Sweeper Keeper","CB":"Defensive CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Press Trigger CM","CDM":"Ball-Winning CM","ST":"Pressing Striker"}},

  {name:"Grassroots 3-5-2",     category:"Direct",         color:"#16a34a", formation:"3-5-2",
   description:"Simple 3-5-2 for teams with good wing-backs. Provides defensive solidity with attacking width. Two strikers work as a partnership.",
   principles:["Three CBs provide defensive security","Wing-backs get up and down — they are your wide players","Midfield three work hard to win and keep the ball","Two strikers work together — hold and run","Keep it simple — work hard and make it hard to play against"],
   roles:{"GK":"Shot-stopper","CB":"Aerial CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","ST":"Target Man"}},

  /* ══ 3-4-3 — 8 systems ══ */
  {name:"Alonso 3-4-3",         category:"Possession",     color:"#E32221", formation:"3-4-3",
   description:"3-back possession system. Wing-backs attack aggressively and recover quickly. Two 8s control central zones. Inverted wingers cut inside.",
   principles:["3-back build-up draws the press then plays through it","Wing-backs attack aggressively and recover quickly","Central midfield pair controls the tempo","Wingers cut inside to create 3v2 in the final third","High defensive line — press immediately after losing the ball"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","LW":"Inverted Winger","ST":"Clinical Finisher"}},

  {name:"Guardiola 3-4-3",      category:"Possession",     color:"#5bc5f5", formation:"3-4-3",
   description:"Extreme overload system. 3 CBs + 4 midfielders control possession. Wing-backs are the only width. Three forwards press high.",
   principles:["Extreme positional superiority through 3-4-3 structure","Wing-backs are the only wide outlet — must be mobile","Three forwards press the defensive line relentlessly","CBs must be comfortable with the ball under pressure","Constant movement — no player stays static"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Holding Pivot","RW":"Inverted Winger","LW":"Inverted Winger","ST":"False 9"}},

  {name:"Tuchel 3-4-3",         category:"High Press",     color:"#034694", formation:"3-4-3",
   description:"Fluid 3-4-3 high press. Wing-backs create numerical overloads. Three forwards press in coordinated waves. Becomes 5-2-3 in defence.",
   principles:["Three forwards press in coordinated waves from the front","Wing-backs create 2v1 situations on both flanks","System becomes 5-2-3 in defensive phase — compact","Ball-winning CM is the defensive anchor","Quick transitions — attack immediately after winning the ball"],
   roles:{"GK":"Sweeper Keeper","CB":"Defensive CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Ball-Winning CM","CDM":"Holding Pivot","RW":"Wide Forward","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Conte 3-4-3",          category:"Counter-attack", color:"#0057A8", formation:"3-4-3",
   description:"Attacking 3-4-3. Wing-backs push high to create 5v4 overload in attack. Three forwards press and combine in the final third.",
   principles:["Wing-backs push into attacking positions — create 5v4 overloads","Three CBs defend the space the wing-backs leave","Three forwards press and combine — fluid movement","Ball-winning CM protects the three CBs from counter-attacks","Exploit wing-back spaces quickly on the transition"],
   roles:{"GK":"Shot-stopper","CB":"Aerial CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Ball-Winning CM","CDM":"Deep Pivot","RW":"Wide Forward","LW":"Wide Forward","ST":"Target Man"}},

  {name:"Klopp 3-4-3",          category:"High Press",     color:"#c8102e", formation:"3-4-3",
   description:"Gegenpressing in a 3-4-3. Three forwards press the CBs. Wing-backs press the full-backs. CMs mop up and drive forward in transition.",
   principles:["Three forwards press the CBs from minute one","Wing-backs press the full-backs — no easy outlets","CMs mop up and drive forward in transition","Three CBs hold a high defensive line — brave and coordinated","Win the ball high and attack immediately — vertical transitions"],
   roles:{"GK":"Sweeper Keeper","CB":"Defensive CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Press Trigger CM","CDM":"Box-to-Box CM","RW":"Wide Forward","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Enrique 3-4-3",        category:"Possession",     color:"#AA151B", formation:"3-4-3",
   description:"Attacking 3-4-3 with maximum width. Wing-backs create the width, three technical forwards combine in the final third. Dominant possession.",
   principles:["Maximum width — wing-backs and wide forwards stretch the opposition","Three technical forwards combine in tight spaces in the final third","Dominant possession — control the tempo from the back","Press from the front — trap the opposition build-up","Three CBs provide stability when wing-backs attack"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Creative CM","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","LW":"Wide Forward","ST":"False 9"}},

  {name:"Nagelsmann 3-4-3",     category:"Possession",     color:"#e63946", formation:"3-4-3",
   description:"High pressing 3-4-3 with positional play principles. Three CBs build from the back. Wing-backs attack. Fluid interchange of the three forwards.",
   principles:["Three CBs build from the back under pressure — brave and technical","Wing-backs provide width and deliver into the box","Three forwards interchange positions — fluid and unpredictable","Press immediately after losing the ball","Dominate possession in the middle third"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Box-to-Box CM","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","LW":"Wide Forward","ST":"Clinical Finisher"}},

  {name:"Bielsa 3-4-3",         category:"High Press",     color:"#FFFFFF", formation:"3-4-3",
   description:"Ultra-aggressive man-marking press in a 3-4-3. Every player marks an opponent. Wing-backs press opposition full-backs. Three forwards press three CBs.",
   principles:["Man-marking across the pitch — every player has an opponent","Wing-backs press the opposition full-backs tightly","Three forwards press the three CBs — no comfortable build-up","Physical intensity for 90 minutes — the system demands it","Win the ball high and attack immediately — no hesitation"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","RWB":"Wing-back","LWB":"Wing-back","CM":"Press Trigger CM","CDM":"Ball-Winning CM","RW":"Wide Forward","LW":"Wide Forward","ST":"Pressing Striker"}},

  /* ══ 4-4-2 Diamond — 4 systems ══ */
  {name:"Ferguson Diamond",     category:"Counter-attack", color:"#DA291C", formation:"4-4-2 Diamond",
   description:"Classic Ferguson diamond. CDM screens, two CMs combine, CAM creates, two strikers exploit. Fluid, direct, dangerous.",
   principles:["CDM screens the back four — the defensive anchor","Two CMs provide energy and creativity","CAM is the playmaker — receives between the lines","Two strikers work as a pair — hold and run","Quick direct passes through the diamond"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Aerial CB","LB":"Defensive Full-back","CDM":"Holding Pivot","CM":"Box-to-Box CM","CAM":"CAM / No.10","ST":"Clinical Finisher"}},

  {name:"Pirlo Diamond",        category:"Possession",     color:"#003087", formation:"4-4-2 Diamond",
   description:"Deep playmaker anchors the diamond. Full-backs provide width. No.10 free to create. Two strikers combine in the final third.",
   principles:["Deep playmaker dictates from the base of the diamond","Full-backs push high to create width","No.10 operates freely — the creative heart of the system","Two strikers rotate — one hold, one mobile","Quick triangles through the diamond — always a passing option"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","CM":"Creative CM","CAM":"Shadow Striker","ST":"Clinical Finisher"}},

  {name:"Mourinho Diamond",     category:"Low Block",      color:"#0047AB", formation:"4-4-2 Diamond",
   description:"Defensive diamond. CDM sits deep, two CMs work hard in both directions. CAM links play on the counter. Two strikers press high then run in behind.",
   principles:["CDM sits deep to protect the back four","Two CMs cover enormous ground in both directions","CAM links play on the counter — find the striker quickly","Two strikers press high from the front — force errors","Compact shape — make it very difficult to play through"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","CM":"Press Trigger CM","CAM":"Shadow Striker","ST":"Target Man"}},

  {name:"Zidane Diamond",       category:"Counter-attack", color:"#FFFFFF", formation:"4-4-2 Diamond",
   description:"Counter-attacking diamond with world-class talent at every position. Defence is solid, No.10 is the key, two strikers are lethal.",
   principles:["Defensive solidity is the foundation — never be exposed","No.10 is the creative key — give them freedom to play","Two strikers work as a pair — one holds, one runs","Win the ball and use the No.10 to start the counter","Individual quality wins games — trust your players"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","CM":"Box-to-Box CM","CAM":"CAM / No.10","ST":"Clinical Finisher"}},

  /* ══ 4-1-4-1 — 2 systems ══ */
  {name:"Howe 4-1-4-1",         category:"High Press",     color:"#000000", formation:"4-1-4-1",
   description:"High energy 4-1-4-1. Four midfielders press in a line. CDM screens. Lone striker works hard and combines with arriving midfielders.",
   principles:["Four midfielders press in a coordinated line","CDM screens the back four — covers the pressing midfielders","Wide midfielders track back and defend their zone","Lone striker works tirelessly — press and hold","Midfielders arrive late in the box — the second wave of attack"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","RM":"Wide Forward","CM":"Box-to-Box CM","LM":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Benitez 4-1-4-1",      category:"Low Block",      color:"#C8102E", formation:"4-1-4-1",
   description:"Organised defensive 4-1-4-1. Single pivot protects. Four-man midfield is compact. Counter through the lone striker on the break.",
   principles:["Single pivot protects the back four at all times","Four-man midfield creates a compact defensive unit","Wide midfielders tuck inside to create a narrow block","Lone striker holds the ball and waits for the counter","Win the ball and find the striker immediately on the break"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","RM":"Wide Forward","CM":"Ball-Winning CM","LM":"Wide Forward","ST":"Clinical Finisher"}},

  /* ══ 5-4-1 — 2 systems ══ */
  {name:"Nuno 5-4-1",           category:"Low Block",      color:"#FDB913", formation:"5-4-1",
   description:"Compact 5-4-1. Wing-backs track wide players. Midfield four is tight and hard-working. One striker presses aggressively and waits for the counter.",
   principles:["Compact defensive 5-4-1 — very difficult to break down","Wing-backs track the opposition's wide players","Midfield four works as a collective unit — no gaps","One striker presses aggressively and waits for the counter","Fast transition — win it and find the striker immediately"],
   roles:{"GK":"Shot-stopper","CB":"Defensive CB","RWB":"Wing-back","LWB":"Wing-back","RM":"Wide Forward","CM":"Ball-Winning CM","LM":"Wide Forward","ST":"Pressing Striker"}},

  {name:"Simeone 5-4-1",        category:"Low Block",      color:"#CB3524", formation:"5-4-1",
   description:"Ultra-defensive 5-4-1. Six defenders behind the ball in certain situations. Win the ball and counter through the striker or wide players.",
   principles:["Ultra-compact block — six defenders when needed","Wing-backs track opposition wide players tightly","Midfield four defends in a narrow line","Single striker is the outlet — must hold the ball well","Counter with pace — exploit the spaces the opposition leaves"],
   roles:{"GK":"Shot-stopper","CB":"Aerial CB","RWB":"Defensive Full-back","LWB":"Defensive Full-back","RM":"Wide Forward","CM":"Ball-Winning CM","LM":"Wide Forward","ST":"Target Man"}},

  /* ══ 4-3-1-2 — 4 systems ══ */
  {name:"Lippi 4-3-1-2",         category:"Possession",     color:"#003087", formation:"4-3-1-2",
   description:"The Christmas tree. No wingers. CAM creates through the centre. Two strikers work as a pair. Midfield trio controls possession.",
   principles:["No wingers — the full-backs provide width","CAM operates freely between the lines","Two strikers combine — one holds, one runs","Midfield trio control possession in the centre","Full-backs must be dynamic — they are the only width"],
   roles:{"GK":"Shot-stopper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","CM":"Box-to-Box CM","CAM":"CAM / No.10","ST":"Clinical Finisher"}},

  {name:"Capello 4-3-1-2",       category:"Counter-attack", color:"#CC0000", formation:"4-3-1-2",
   description:"Compact counter-attacking Christmas tree. Defensive midfield base, creative No.10, two strikers in partnership. Hard to break down.",
   principles:["Three midfielders defend deep then transition quickly","No.10 is the creative link — find space between the lines","Two strikers press from the front — force errors","Compact defensive shape — narrow and hard to play through","Counter quickly through the No.10 to the strikers"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","CM":"Press Trigger CM","CAM":"Shadow Striker","ST":"Target Man"}},

  {name:"Sacchi 4-3-1-2",        category:"High Press",     color:"#CC0000", formation:"4-3-1-2",
   description:"High pressing Christmas tree. The No.10 activates the press. Midfield trio presses in waves. Two strikers hunt the ball from the front.",
   principles:["No.10 triggers the press by closing the CDM","Midfield trio presses in coordinated waves","Two strikers hunt the CBs — no comfortable build-up","Defensive line is very high — brave and coordinated","Win the ball high and use the No.10 to create immediately"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","CM":"Press Trigger CM","CAM":"CAM / No.10","ST":"Pressing Striker"}},

  {name:"Del Bosque 4-3-1-2",    category:"Possession",     color:"#AA151B", formation:"4-3-1-2",
   description:"Spanish tiki-taka Christmas tree. No wingers. Deep playmaker, dynamic CMs, creative No.10, two technical strikers. Total possession control.",
   principles:["Deep playmaker dictates from the base of the midfield","CMs combine with the No.10 in tight central spaces","Two strikers interchange and combine in the final third","Full-backs push high to create width — the system demands it","Never lose the ball — possession is the primary weapon"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","CM":"Creative CM","CAM":"CAM / No.10","ST":"False 9"}},

  /* ══ 4-5-1 — 3 systems ══ */
  {name:"Defensive 4-5-1",       category:"Low Block",      color:"#1a1612", formation:"4-5-1",
   description:"Compact five-man midfield. Hard to break down. One striker works tirelessly alone. Counter through the wide midfielders.",
   principles:["Five in midfield creates a compact central block","Wide midfielders track back and defend their zones tightly","Lone striker presses intelligently and holds the ball","Counter through the wide midfielders who carry the ball","Win your individual duels — the team wins when you win"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CDM":"Deep Pivot","CM":"Ball-Winning CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Target Man"}},

  {name:"Pressing 4-5-1",        category:"High Press",     color:"#2e7d7a", formation:"4-5-1",
   description:"High press from a 4-5-1. Five-man midfield presses aggressively. Lone striker triggers the press from the front. Wide midfielders join the press.",
   principles:["Lone striker triggers the press — activates the five-man midfield","Wide midfielders join the press immediately","Central CM is the defensive anchor — covers the pressing players","Five-man midfield wins the ball in the middle third","Attack immediately after winning the ball — quick transitions"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Holding Pivot","CM":"Press Trigger CM","RM":"Wide Forward","LM":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Possession 4-5-1",      category:"Possession",     color:"#005daa", formation:"4-5-1",
   description:"Possession-dominant 4-5-1. Five midfielders control the game. Lone striker works hard to hold and link. Full-backs push high.",
   principles:["Five midfielders dominate possession in the central and wide zones","Lone striker holds the ball and links — the focal point","Full-backs push high to create width and additional passing options","Central CM dictates the tempo — the conductor of the team","Press immediately after losing the ball — five-man midfield makes this possible"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","CM":"Creative CM","RM":"Wide Forward","LM":"Wide Forward","ST":"Clinical Finisher"}},

  /* ══ 4-4-1-1 — 3 systems ══ */
  {name:"Compact 4-4-1-1",       category:"Low Block",      color:"#1a1612", formation:"4-4-1-1",
   description:"Compact 4-4-1-1. Two lines of four + a No.10 + lone striker. Extremely hard to break down. Counter through the No.10 to the striker.",
   principles:["Two banks of four are compact and organised","No.10 is the outlet — finds the ball and turns","Lone striker makes the run in behind on the counter","Wide midfielders defend their zones tightly","Win the ball low and use the No.10 to start the attack"],
   roles:{"GK":"Shot-stopper","RB":"Defensive Full-back","CB":"Defensive CB","LB":"Defensive Full-back","CM":"Ball-Winning CM","RM":"Wide Forward","LM":"Wide Forward","CAM":"Shadow Striker","ST":"Clinical Finisher"}},

  {name:"Creative 4-4-1-1",      category:"Possession",     color:"#AE0025", formation:"4-4-1-1",
   description:"Possession 4-4-1-1 with a free-roaming No.10. Four midfielders work hard. The No.10 is given licence to find space and create.",
   principles:["Four midfielders work hard in both directions","No.10 is given total freedom — find the ball wherever you can","Lone striker works hard and provides the focal point","Full-backs push high when the team has possession","The No.10 is the key — the game goes through them"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Box-to-Box CM","RM":"Inverted Winger","LM":"Wide Forward","CAM":"CAM / No.10","ST":"Pressing Striker"}},

  {name:"Press 4-4-1-1",         category:"High Press",     color:"#c8102e", formation:"4-4-1-1",
   description:"High press 4-4-1-1. No.10 triggers the press from behind the striker. Four midfielders press in waves. Lone striker hunts the ball.",
   principles:["No.10 triggers the press — cuts the passing lane to the CDM","Lone striker presses the CBs after the trigger","Four midfielders press in coordinated waves — not simultaneously","Win the ball in the middle third and attack immediately","No.10 links the transitions — find the striker or the wide players"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CM":"Press Trigger CM","RM":"Wide Forward","LM":"Inverted Winger","CAM":"Shadow Striker","ST":"Pressing Striker"}},

  /* ══ 4-3-3 Possession — 3 systems ══ */
  {name:"Positional 4-3-3",      category:"Possession",     color:"#5bc5f5", formation:"4-3-3 Possession",
   description:"Pure positional play variant. Double CDM controls the base. Three forwards stay high and wide. Full-backs tuck inside.",
   principles:["Double CDM provides the positional base — stability first","Full-backs tuck inside into the double pivot — create 4 in midfield","Three forwards stay high and wide — stretch the opposition","Ball must always move quickly — no more than 3 seconds on the ball","Win the ball back immediately — the counter-press is non-negotiable"],
   roles:{"GK":"Sweeper Keeper","RB":"Inverted Full-back","CB":"Ball-playing CB","LB":"Inverted Full-back","CDM":"Holding Pivot","RW":"Wide Forward","LW":"Inverted Winger","ST":"False 9"}},

  {name:"Tiki-Taka 4-3-3",       category:"Possession",     color:"#AA151B", formation:"4-3-3 Possession",
   description:"Peak tiki-taka. Three CDMs recycle possession. Three technical forwards combine. The team never loses the ball — possession is everything.",
   principles:["Three midfielders recycle possession — short, quick, accurate","Three forwards combine in one-two touch — never hold the ball","Full-backs push high and stay high — they are the width","Press immediately after losing the ball — win it back in 5 seconds","No long balls — ever"],
   roles:{"GK":"Sweeper Keeper","RB":"Attacking Full-back","CB":"Ball-playing CB","LB":"Attacking Full-back","CDM":"Deep-Lying Playmaker","RW":"Inverted Winger","LW":"Wide Forward","ST":"False 9"}},

  {name:"Overload 4-3-3",        category:"Possession",     color:"#16a34a", formation:"4-3-3 Possession",
   description:"Central overload 4-3-3. Three deep midfielders create numerical superiority in the centre. Three forwards stretch the opposition. Full-backs stay.",
   principles:["Three midfielders create a central overload — 3v2 or 3v1 in the middle","Three forwards stay high and wide — they provide the goal threat","Full-backs stay disciplined — they are the defensive width","Possession is recycled through the three midfielders under pressure","When the forward runs, the CM supports and arrives in the box"],
   roles:{"GK":"Sweeper Keeper","RB":"Defensive Full-back","CB":"Ball-playing CB","LB":"Defensive Full-back","CDM":"Holding Pivot","RW":"Inverted Winger","LW":"Wide Forward","ST":"Clinical Finisher"}},

  /* ══ 3-3-3-1 — 3 systems ══ */
  {name:"Fluid 3-3-3-1",         category:"Possession",     color:"#5bc5f5", formation:"3-3-3-1",
   description:"Ultra-fluid system. Three CBs. Three midfielders. Three attack-minded players behind the striker. Maximum creativity, maximum risk.",
   principles:["Three CBs provide the defensive base — brave and comfortable on the ball","Three midfielders link and create — constant movement","Three behind the striker create overloads in the final third","Lone striker is the focal point — hold, link and score","This system demands technical quality in every position"],
   roles:{"GK":"Sweeper Keeper","CB":"Ball-playing CB","CDM":"Holding Pivot","CM":"Creative CM","RW":"Inverted Winger","CAM":"CAM / No.10","LW":"Wide Forward","ST":"False 9"}},

  {name:"Press 3-3-3-1",         category:"High Press",     color:"#c8102e", formation:"3-3-3-1",
   description:"High pressing 3-3-3-1. Three forwards press aggressively behind the striker. Three midfielders press in the next wave. Three CBs hold a high line.",
   principles:["Three attacking players behind the striker press from the front","Three midfielders press in the second wave immediately after","Three CBs hold a very high defensive line — they trust the GK","Lone striker hunts the ball — triggers the whole press","Win the ball high and attack immediately — vertical transitions"],
   roles:{"GK":"Sweeper Keeper","CB":"Defensive CB","CDM":"Ball-Winning CM","CM":"Press Trigger CM","RW":"Wide Forward","CAM":"Shadow Striker","LW":"Inverted Winger","ST":"Pressing Striker"}},

  {name:"Counter 3-3-3-1",       category:"Counter-attack", color:"#0047AB", formation:"3-3-3-1",
   description:"Counter-attacking 3-3-3-1. Compact three-CB block. Three midfielders win the ball. Three attack-minded players transition. Lone striker finishes.",
   principles:["Three CBs provide a compact defensive block","Three midfielders win the ball and recycle — they are the engine","Three attack-minded players transition immediately on the counter","Lone striker makes the run in behind — the focal point of the attack","Counter with pace — the three attack-minded players carry the ball forward quickly"],
   roles:{"GK":"Shot-stopper","CB":"Defensive CB","CDM":"Holding Pivot","CM":"Ball-Winning CM","RW":"Wide Forward","CAM":"CAM / No.10","LW":"Inverted Winger","ST":"Clinical Finisher"}},

];


function posGroup(pos){
  if(pos==="GK") return "gk";
  if(["CB","RB","LB","RWB","LWB"].includes(pos)) return "def";
  if(["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}
function attrC(v){return v>=80?"av-hi":v>=65?"av-md":"av-lo";}
function ini(n){return n&&n.split(" ").map(w=>w[0]).join("")||"?";}

/* ══ OPPOSITION SYSTEM PICKER ══
   Full searchable library of all 65 systems — not filtered by formation.
   Clicking any system applies its formation AND roles to the opposition.
════════════════════════════════ */
function OppSystemPicker({ oppFormation, oppSystem, onPick }) {
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");

  const cats = ["All","Possession","High Press","Counter-attack","Low Block","Direct"];

  const filtered = SYSTEM_PRESETS.filter(s => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      s.name.toLowerCase().includes(q) ||
      s.formation.includes(q) ||
      s.category.toLowerCase().includes(q) ||
      (s.description||"").toLowerCase().includes(q);
    const matchCat = catFilter==="All" || s.category===catFilter;
    return matchQ && matchCat;
  });

  // Group by whether they match the current opp formation
  const matching = filtered.filter(s=>s.formation===oppFormation);
  const others   = filtered.filter(s=>s.formation!==oppFormation);

  const SystemBtn = ({ s }) => (
    <button
      key={s.name}
      onClick={()=>onPick(s)}
      style={{
        width:"100%", padding:"0.38rem 0.6rem", textAlign:"left",
        fontFamily:"'DM Sans',sans-serif", fontSize:"0.68rem", fontWeight:700,
        background:oppSystem?.name===s.name?"rgba(139,42,42,0.45)":"rgba(255,255,255,0.04)",
        border:`1px solid ${oppSystem?.name===s.name?"var(--darkred)":"rgba(255,255,255,0.09)"}`,
        color:"rgba(255,255,255,0.85)", cursor:"pointer",
        display:"flex", alignItems:"center", gap:"0.4rem",
        transition:"all 0.15s",
      }}
      onMouseEnter={e=>{if(oppSystem?.name!==s.name){e.currentTarget.style.background="rgba(255,255,255,0.09)";e.currentTarget.style.borderColor="rgba(255,255,255,0.2)";}}}
      onMouseLeave={e=>{if(oppSystem?.name!==s.name){e.currentTarget.style.background="rgba(255,255,255,0.04)";e.currentTarget.style.borderColor="rgba(255,255,255,0.09)";}}}
    >
      <div style={{width:8,height:8,borderRadius:"50%",background:s.color,flexShrink:0,border:s.color==="#FFFFFF"?"1px solid #888":""}}/>
      <div style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.6rem",letterSpacing:"1px",color:"rgba(255,255,255,0.3)",flexShrink:0}}>{s.formation}</span>
    </button>
  );

  return (
    <div>
      {/* Search */}
      <input
        value={search}
        onChange={e=>setSearch(e.target.value)}
        placeholder="Search system, manager, formation..."
        style={{
          width:"100%", background:"rgba(0,0,0,0.35)",
          border:"1px solid rgba(255,255,255,0.12)",
          borderBottom:"2px solid var(--darkred)",
          color:"rgba(255,255,255,0.85)", fontFamily:"'DM Sans',sans-serif",
          fontSize:"0.72rem", padding:"0.38rem 0.6rem", outline:"none",
          marginBottom:"0.4rem",
        }}
      />

      {/* Category filter */}
      <div style={{display:"flex",gap:"0.25rem",flexWrap:"wrap",marginBottom:"0.45rem"}}>
        {cats.map(c=>(
          <button key={c} onClick={()=>setCatFilter(c)}
            style={{padding:"0.16rem 0.5rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.55rem",fontWeight:800,letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer",border:"1px solid",transition:"all 0.15s",
              background:catFilter===c?"var(--darkred)":"transparent",
              borderColor:catFilter===c?"var(--darkred)":"rgba(255,255,255,0.15)",
              color:catFilter===c?"white":"rgba(255,255,255,0.45)"}}>
            {c}
          </button>
        ))}
      </div>

      {/* Results */}
      <div style={{maxHeight:220,overflowY:"auto",display:"flex",flexDirection:"column",gap:"0.18rem"}}>
        {matching.length>0&&(
          <>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.52rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--darkred)",padding:"0.15rem 0",borderBottom:"1px solid rgba(139,42,42,0.3)",marginBottom:"0.15rem"}}>
              Fits {oppFormation} · {matching.length}
            </div>
            {matching.map(s=><SystemBtn key={s.name} s={s}/>)}
          </>
        )}
        {others.length>0&&(
          <>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.52rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.25)",padding:"0.2rem 0",borderBottom:"1px solid rgba(255,255,255,0.06)",marginBottom:"0.15rem",marginTop:matching.length>0?"0.35rem":0}}>
              All Systems · {others.length}
            </div>
            {others.map(s=><SystemBtn key={s.name} s={s}/>)}
          </>
        )}
        {filtered.length===0&&(
          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"rgba(255,255,255,0.3)",fontStyle:"italic",padding:"0.5rem 0",textAlign:"center"}}>
            No systems match "{search}"
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN TACTICS BOARD COMPONENT
══════════════════════════════════════ */

/* ── BOARD SAVE/LOAD HELPERS ── */
const BOARD_KEY = "tactics_board_state";
const SAVED_BOARDS_KEY = "tactics_saved_boards";

function loadBoardState() {
  return loadFromStorage(BOARD_KEY, null);
}

function saveBoardState(state) {
  saveToStorage(BOARD_KEY, state);
}

async function saveToCloud(boardState, boardName) {
  try {
    const sb = getSupabaseClient();
    if (!sb) return;
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return;

    const row = {
      user_id: user.id,
      name: boardName || "My Tactics Board",
      formation: boardState.formation,
      tokens: boardState.tokens,
      arrows: boardState.arrows,
      notes: boardState.notes,
      updated_at: new Date().toISOString(),
    };

    // Upsert by user_id + name
    const existing = await sb.from("tactics_boards").select("id").eq("user_id", user.id).eq("name", row.name).single();
    if (existing.data?.id) {
      await sb.from("tactics_boards").update(row).eq("id", existing.data.id);
    } else {
      await sb.from("tactics_boards").insert([row]);
    }
  } catch(e) {
    console.warn("Board cloud save failed:", e.message);
  }
}

async function loadFromCloud() {
  try {
    const sb = getSupabaseClient();
    if (!sb) return null;
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return null;

    const { data } = await sb.from("tactics_boards")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });
    return data || [];
  } catch(e) {
    return null;
  }
}

export default function TacticsBoard({squadPlayers=[], onNavigateToSquad=null}) {
  // Load persisted state on first render
  const savedState = useMemo(() => loadBoardState(), []);

  const [formation, setFormation] = useState(savedState?.formation || "4-3-3");
  const [tokens, setTokens] = useState(savedState?.tokens || []);
  const [selectedToken, setSelectedToken] = useState(null);
  const [rightTab, setRightTab] = useState("board");
  const [tool, setTool] = useState("select");
  const [arrows, setArrows] = useState(savedState?.arrows || []);
  const [cones, setCones] = useState(savedState?.cones || []);
  const [annotations, setAnnotations] = useState(savedState?.annotations || []);
  const [arrowStart, setArrowStart] = useState(null);
  const [currentArrow, setCurrentArrow] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [notes, setNotes] = useState(savedState?.notes || {});
  const [arrowColor, setArrowColor] = useState(savedState?.arrowColor || "#FFD700");
  const [showGrid, setShowGrid] = useState(false);
  const [hoveredToken, setHoveredToken] = useState(null);

  // ── OPPOSITION TEAM ──
  const [showOpp, setShowOpp] = useState(false);
  const [oppFormation, setOppFormation] = useState("4-4-2");
  const [oppTokens, setOppTokens] = useState([]);
  const [oppSystem, setOppSystem] = useState(null);
  const [selectedOppToken, setSelectedOppToken] = useState(null);
  const [hoveredOppToken, setHoveredOppToken] = useState(null);

  // Mirror formation slots to the top half of pitch (flip y, swap left/right)
  const mirrorSlots = (slots) => slots.map((slot, i) => ({
    ...slot,
    id: `opp-${i}-${Date.now()}`,
    x: 100 - slot.x,          // mirror horizontally (RB becomes LB side)
    y: 100 - slot.y,          // mirror vertically (GK at top)
    team: "b",
    player: null,
    role: null,
    isOpp: true,
  }));

  const applyOppFormation = (form) => {
    setOppFormation(form);
    const slots = FORMATIONS[form] || FORMATIONS["4-4-2"];
    setOppTokens(mirrorSlots(slots));
  };

  const applyOppSystem = (system) => {
    // Apply the system's formation (may differ from current oppFormation)
    const targetFormation = system.formation;
    setOppFormation(targetFormation);
    const slots = FORMATIONS[targetFormation] || FORMATIONS["4-4-2"];
    const mirrored = mirrorSlots(slots);
    setOppTokens(mirrored);
    setOppSystem(system);
    // Apply roles after tokens are set
    setTimeout(() => {
      setOppTokens(prev => prev.map(t => ({
        ...t,
        role: system.roles[t.pos] || t.role,
      })));
    }, 50);
  };

  // Show opposition — init with default formation
  const toggleOpp = () => {
    if (!showOpp && oppTokens.length === 0) {
      applyOppFormation(oppFormation);
    }
    setShowOpp(s => !s);
  }; // id of hovered token for tooltip

  // Cycle through roles for a token's position group
  const cycleRole = (e, tokenId) => {
    e.stopPropagation();
    const token = tokens.find(t => t.id === tokenId);
    if (!token) return;
    const posRoleMap = {
      gk:  ["Sweeper Keeper","Shot-stopper"],
      def: ["Ball-playing CB","Defensive CB","Aerial CB","Attacking Full-back","Defensive Full-back","Inverted Full-back","Wing-back"],
      mid: ["Holding Pivot","Deep Pivot","Box-to-Box CM","Creative CM","Press Trigger CM","Ball-Winning CM","Deep-Lying Playmaker","CAM / No.10","Attacking Midfielder","Shadow Striker"],
      att: ["Inverted Winger","Wide Forward","Clinical Finisher","Target Man","False 9","Pressing Striker","Second Striker"],
    };
    const grp = token.group || posGroup(token.pos);
    const validRoles = posRoleMap[grp] || Object.keys(ROLE_DATA);
    const currentIdx = validRoles.indexOf(token.role);
    const nextRole = validRoles[(currentIdx + 1) % validRoles.length];
    setTokens(prev => prev.map(t => t.id === tokenId ? {...t, role: nextRole} : t));
  };

  // Save state management
  const [savedBoards, setSavedBoards] = useState(() => loadFromStorage(SAVED_BOARDS_KEY, []));
  const [boardName, setBoardName] = useState(savedState?.boardName || "My Tactics Board");
  const [saveStatus, setSaveStatus] = useState(""); // "" | "saving" | "saved" | "error"
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [cloudBoards, setCloudBoards] = useState([]);
  const [loadingCloud, setLoadingCloud] = useState(false);
  const autoSaveRef = useRef(null);

  // Auto-save to localStorage whenever state changes (debounced 800ms)
  useEffect(() => {
    if (tokens.length === 0) return; // Don't overwrite with empty state
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      saveBoardState({ formation, tokens, arrows, cones, annotations, notes, arrowColor, boardName });
    }, 800);
    return () => clearTimeout(autoSaveRef.current);
  }, [formation, tokens, arrows, cones, annotations, notes, arrowColor, boardName]);

  // Manual save — localStorage + Supabase + named boards list
  const saveBoard = async (name) => {
    const n = name || boardName;
    setBoardName(n);
    setSaveStatus("saving");

    const state = { formation, tokens, arrows, cones, annotations, notes, arrowColor, boardName: n, savedAt: new Date().toISOString() };

    // Save as current board
    saveBoardState(state);

    // Save to named boards list
    const updated = [
      { ...state, name: n },
      ...(savedBoards.filter(b => b.name !== n)).slice(0, 9),
    ];
    setSavedBoards(updated);
    saveToStorage(SAVED_BOARDS_KEY, updated);

    // Save to Supabase
    await saveToCloud(state, n);

    setSaveStatus("saved");
    setTimeout(() => setSaveStatus(""), 2500);
    setShowSavePanel(false);
  };

  // Load a saved board
  const loadBoard = (board) => {
    if (board.formation) { setFormation(board.formation); }
    if (board.tokens) setTokens(board.tokens);
    if (board.arrows) setArrows(board.arrows);
    if (board.cones) setCones(board.cones || []);
    if (board.annotations) setAnnotations(board.annotations || []);
    if (board.notes) setNotes(board.notes || {});
    if (board.arrowColor) setArrowColor(board.arrowColor);
    setBoardName(board.name || board.boardName || "My Tactics Board");
    setSelectedToken(null);
    setShowSavePanel(false);
  };

  // Delete a saved board
  const deleteBoard = (name) => {
    const updated = savedBoards.filter(b => b.name !== name);
    setSavedBoards(updated);
    saveToStorage(SAVED_BOARDS_KEY, updated);
  };

  // Load cloud boards
  const fetchCloudBoards = async () => {
    setLoadingCloud(true);
    const boards = await loadFromCloud();
    if (boards) setCloudBoards(boards);
    setLoadingCloud(false);
  };

  const pitchRef = useRef(null);
  const canvasRef = useRef(null);
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;

  /* ── FORMATION CHANGE ── */
  const applyFormation = useCallback((form) => {
    setFormation(form);
    const slots = FORMATIONS[form] || FORMATIONS["4-3-3"];
    setTokens(prev => {
      return slots.map((slot, i) => {
        const existing = prev.find(t => t.pos === slot.pos && !prev.filter(tt => tt.pos === slot.pos).slice(0, i).includes(t));
        return {
          id: existing?.id || nextId(),
          pos: slot.pos,
          x: slot.x,
          y: slot.y,
          num: slot.num,
          player: existing?.player || null,
          role: existing?.role || null,
          team: "a",
        };
      });
    });
  }, []);

  // Only apply default formation if no saved state — don't overwrite a persisted board
  useEffect(() => {
    if (!savedState || !savedState.tokens || savedState.tokens.length === 0) {
      applyFormation("4-3-3");
    }
  }, []);

  /* ── APPLY SYSTEM PRESET ── */
  const [currentSystem, setCurrentSystem] = useState(null);

  const applySystem = (system) => {
    applyFormation(system.formation);
    setCurrentSystem(system);
    setTimeout(() => {
      setTokens(prev => prev.map(t => ({
        ...t,
        role: system.roles[t.pos] || t.role,
      })));
    }, 50);
  };

  /* ── DRAG FROM POOL ── */
  const onDragStartPool = (e, player) => {
    e.dataTransfer.setData("player", JSON.stringify(player));
  };

  /* ── DROP ON PITCH ── */
  const onDropPitch = (e) => {
    e.preventDefault();
    setDragOver(false);
    const playerData = e.dataTransfer.getData("player");
    if (!playerData) return;
    const player = JSON.parse(playerData);
    const rect = pitchRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    // Find nearest empty slot or create free token
    const emptySlot = tokens.find(t => !t.player && Math.abs(t.x - x) < 12 && Math.abs(t.y - y) < 12);
    if (emptySlot) {
      setTokens(prev => prev.map(t => t.id === emptySlot.id ? {...t, player} : t));
    } else {
      // Assign to nearest slot without player
      const nearest = tokens.filter(t => !t.player).sort((a,b) =>
        (Math.abs(a.x-x)+Math.abs(a.y-y)) - (Math.abs(b.x-x)+Math.abs(b.y-y))
      )[0];
      if (nearest) {
        setTokens(prev => prev.map(t => t.id === nearest.id ? {...t, player} : t));
      }
    }
  };

  /* ── DRAG TOKEN ON PITCH ── */
  const onTokenMouseDown = (e, tokenId) => {
    if (tool !== "select") return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedToken(tokenId);
    setIsDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const token = tokens.find(t => t.id === tokenId);
    const startTokenX = token.x;
    const startTokenY = token.y;

    const onMove = (me) => {
      const rect = pitchRef.current.getBoundingClientRect();
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      setTokens(prev => prev.map(t => t.id === tokenId ? {
        ...t,
        x: Math.max(3, Math.min(97, startTokenX + dx)),
        y: Math.max(3, Math.min(97, startTokenY + dy)),
      } : t));
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const onOppTokenMouseDown = (e, tokenId) => {
    if (tool !== "select") return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedOppToken(tokenId);
    setIsDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const token = oppTokens.find(t => t.id === tokenId);
    const startTokenX = token.x;
    const startTokenY = token.y;

    const onMove = (me) => {
      const rect = pitchRef.current.getBoundingClientRect();
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      setOppTokens(prev => prev.map(t => t.id === tokenId ? {
        ...t,
        x: Math.max(3, Math.min(97, startTokenX + dx)),
        y: Math.max(3, Math.min(97, startTokenY + dy)),
      } : t));
    };
    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* ── PITCH CLICK (arrows, cones, text) ── */
  const onPitchClick = (e) => {
    if (isDragging) return;
    const rect = pitchRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (tool === "cone") {
      setCones(prev => [...prev, { id: nextId(), x, y, type: "orange" }]);
    } else if (tool === "text") {
      const text = prompt("Enter annotation text:");
      if (text) setAnnotations(prev => [...prev, { id: nextId(), x, y, text }]);
    } else if (tool === "arrow") {
      if (!arrowStart) {
        setArrowStart({x, y});
      } else {
        setArrows(prev => [...prev, {
          id: nextId(), x1: arrowStart.x, y1: arrowStart.y,
          x2: x, y2: y, color: arrowColor, type: "movement",
        }]);
        setArrowStart(null);
        setCurrentArrow(null);
      }
    } else {
      // Deselect
      setSelectedToken(null);
    }
  };

  const onPitchMouseMove = (e) => {
    if (tool === "arrow" && arrowStart) {
      const rect = pitchRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setCurrentArrow({x1: arrowStart.x, y1: arrowStart.y, x2: x, y2: y});
    }
  };

  /* ── DRAW ARROWS ON CANVAS ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pitchRef.current) return;
    const rect = pitchRef.current.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawArrow = (x1, y1, x2, y2, color, dashed=false) => {
      const px1 = (x1/100)*canvas.width;
      const py1 = (y1/100)*canvas.height;
      const px2 = (x2/100)*canvas.width;
      const py2 = (y2/100)*canvas.height;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      if (dashed) ctx.setLineDash([6,4]);
      else ctx.setLineDash([]);
      ctx.moveTo(px1, py1);
      ctx.lineTo(px2, py2);
      ctx.stroke();
      // Arrowhead
      const angle = Math.atan2(py2 - py1, px2 - px1);
      const headLen = 14;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(px2, py2);
      ctx.lineTo(px2 - headLen*Math.cos(angle - Math.PI/6), py2 - headLen*Math.sin(angle - Math.PI/6));
      ctx.moveTo(px2, py2);
      ctx.lineTo(px2 - headLen*Math.cos(angle + Math.PI/6), py2 - headLen*Math.sin(angle + Math.PI/6));
      ctx.stroke();
    };

    arrows.forEach(a => drawArrow(a.x1, a.y1, a.x2, a.y2, a.color));
    if (currentArrow) drawArrow(currentArrow.x1, currentArrow.y1, currentArrow.x2, currentArrow.y2, arrowColor, true);
  }, [arrows, currentArrow, arrowColor]);

  /* ── REMOVE PLAYER FROM SLOT ── */
  const removePlayerFromSlot = (tokenId) => {
    setTokens(prev => prev.map(t => t.id === tokenId ? {...t, player: null, role: null} : t));
    setSelectedToken(null);
  };

  /* ── ASSIGN ROLE ── */
  const assignRole = (tokenId, role) => {
    setTokens(prev => prev.map(t => t.id === tokenId ? {...t, role} : t));
  };

  const selectedTokenData = tokens.find(t => t.id === selectedToken);
  const pitchPlayers = tokens.filter(t => t.player);
  const poolPlayers = squadPlayers.filter(p => !tokens.some(t => t.player?.id === p.id));

  /* ── EXPORT SUMMARY ── */
  const exportSummary = () => {
    const lines = [`FOOTY ANALYST — TACTICS BOARD\nFormation: ${formation}\n`];
    tokens.forEach(t => {
      lines.push(`${t.pos}: ${t.player?.name || "TBC"} — ${t.role || "No role assigned"}`);
    });
    if (Object.values(notes).some(Boolean)) {
      lines.push("\nNOTES:");
      Object.entries(notes).forEach(([id, note]) => {
        const t = tokens.find(tt => tt.id === Number(id));
        if (t && note) lines.push(`${t.player?.name || t.pos}: ${note}`);
      });
    }
    const text = lines.join("\n");
    const blob = new Blob([text], {type: "text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tactics-board.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <style>{TB_CSS}</style>
      <div className="tb-wrap">

        {/* ══ LEFT PANEL ══ */}
        <div className="tb-left">
          <div className="tb-left-header">
            <div className="tb-left-title">Tactics Board</div>
            <div className="tb-left-sub">
              {squadPlayers.length > 0
                ? `✦ ${squadPlayers.length} players from your squad`
                : "Drag players · Set roles · Build your system"}
            </div>
          </div>

          {/* Formation picker */}
          <div className="tb-formation-bar">
            <div className="tb-formation-label">Formation</div>
            <div className="tb-formations">
              {Object.keys(FORMATIONS).map(f => (
                <button key={f} className={`tb-form-btn ${formation===f?"on":""}`} onClick={()=>applyFormation(f)}>{f}</button>
              ))}
            </div>
          </div>

          {/* Player pool */}
          <div className="tb-player-pool">
            <div className="tb-pool-label">
              <span>Squad Players</span>
              <span className="tb-pool-count">{poolPlayers.length} available</span>
            </div>
            {squadPlayers.length === 0 ? (
              <div className="tb-empty" style={{padding:"1.5rem 0.5rem"}}>
                <div className="tb-empty-icon">👥</div>
                <div className="tb-empty-title" style={{fontSize:"0.95rem"}}>No Squad Built</div>
                <div className="tb-empty-sub" style={{fontSize:"0.7rem"}}>Add your players in the Squad Builder, then they'll appear here automatically.</div>
                {onNavigateToSquad&&(
                  <button onClick={onNavigateToSquad} style={{marginTop:"0.85rem",width:"100%",padding:"0.6rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                    → Build My Squad
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Show unassigned pool players */}
                {poolPlayers.length > 0 && (
                  <>
                    <div className="tb-pool-label" style={{marginTop:"0.3rem"}}>Drag to pitch →</div>
                    {poolPlayers.map(p => (
                      <div key={p.id} className="tb-pool-player" draggable onDragStart={e=>onDragStartPool(e,p)}>
                        <div className={`tb-pool-av`} style={{background:p.group==="gk"?"#f59e0b":p.group==="def"?"var(--darkred)":p.group==="mid"?"var(--teal)":"#16a34a"}}>{ini(p.name)}</div>
                        <div className="tb-pool-info">
                          <div className="tb-pool-name">{p.name}</div>
                          <div style={{fontSize:"0.58rem",color:"var(--muted)",fontStyle:"italic"}}>{p.style}</div>
                        </div>
                        <span className={`tb-pool-pos sp-pos-badge ${p.group}`}>{p.pos}</span>
                        <div className="tb-pool-ovr">{p.overall}</div>
                      </div>
                    ))}
                  </>
                )}
                {/* On pitch */}
                {pitchPlayers.length > 0 && (
                  <>
                    <div className="tb-pool-label" style={{marginTop:"0.6rem",borderTop:"1px solid var(--rule2)",paddingTop:"0.5rem"}}>
                      <span>On Pitch</span>
                      <span className="tb-pool-count">{pitchPlayers.length}/11</span>
                    </div>
                    {pitchPlayers.map(t => (
                      <div key={t.id} className="tb-pool-player on-pitch" onClick={()=>{setSelectedToken(t.id);setRightTab("player");}}>
                        <div className="tb-pool-av">{ini(t.player.name)}</div>
                        <div className="tb-pool-info">
                          <div className="tb-pool-name">{t.player.name}</div>
                          <div style={{fontSize:"0.58rem",color:"var(--teal)",fontStyle:"italic"}}>{t.role||t.pos}</div>
                        </div>
                        <span className={`tb-pool-pos sp-pos-badge ${posGroup(t.pos)}`}>{t.pos}</span>
                      </div>
                    ))}
                  </>
                )}
                {/* Generic tokens if no players assigned yet */}
                {pitchPlayers.length === 0 && poolPlayers.length === 0 && (
                  <div className="tb-empty" style={{padding:"1rem 0.5rem"}}>
                    <div style={{fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic"}}>All players on pitch</div>
                  </div>
                )}
              </>
            )}

            {/* Generic position tokens for empty board */}
            {squadPlayers.length === 0 && (
              <div style={{marginTop:"0.5rem"}}>
                <div className="tb-pool-label">Or use position tokens</div>
                {["GK","CB","RB","LB","CDM","CM","CAM","LW","RW","ST"].map(pos=>(
                  <div key={pos} className="tb-pool-player" draggable
                    onDragStart={e=>onDragStartPool(e,{id:nextId(),name:pos,pos,group:posGroup(pos),overall:75,style:pos,pace:70,shooting:70,passing:70,dribbling:70,defending:70,physical:70})}>
                    <div className={`tb-pool-av sp-pos-badge ${posGroup(pos)}`} style={{borderRadius:"50%",width:26,height:26,fontSize:"0.56rem"}}>{pos}</div>
                    <div className="tb-pool-info"><div className="tb-pool-name">{pos}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* ══ PITCH ══ */}
        <div className="tb-pitch-wrap">
          {/* Toolbar */}
          <div className="tb-toolbar">
            <div className="tb-tool-group">
              <span className="tb-tool-label">Tool</span>
              {[
                {id:"select",label:"✋ Select"},
                {id:"arrow",label:"↗ Arrow"},
                {id:"cone",label:"🔶 Cone"},
                {id:"text",label:"📝 Note"},
              ].map(t=>(
                <button key={t.id} className={`tb-tool-btn ${tool===t.id?"on":""}`} onClick={()=>{setTool(t.id);setArrowStart(null);}}>
                  {t.label}
                </button>
              ))}
            </div>
            {tool==="arrow"&&(
              <div className="tb-tool-group">
                <span className="tb-tool-label">Arrow</span>
                {["#FFD700","#4ade80","#f87171","#60a5fa","#ffffff"].map(c=>(
                  <div key={c} onClick={()=>setArrowColor(c)} style={{width:18,height:18,background:c,border:`2px solid ${arrowColor===c?"white":"rgba(255,255,255,0.2)"}`,cursor:"pointer",borderRadius:"2px"}}/>
                ))}
                {arrowStart&&<span style={{fontSize:"0.62rem",color:"var(--mustard)",fontWeight:700,letterSpacing:"1px"}}>CLICK END POINT →</span>}
              </div>
            )}
            <div className="tb-tool-group">
              <span className="tb-tool-label">Clear</span>
              <button className="tb-tool-btn danger" onClick={()=>setArrows([])}>Arrows</button>
              <button className="tb-tool-btn danger" onClick={()=>setCones([])}>Cones</button>
              <button className="tb-tool-btn danger" onClick={()=>setAnnotations([])}>Notes</button>
              <button className="tb-tool-btn danger" onClick={()=>{setTokens(prev=>prev.map(t=>({...t,player:null,role:null})));setSelectedToken(null);}}>Board</button>
            </div>
            <div className="tb-tool-group" style={{marginLeft:"auto"}}>
              <button
                className="tb-tool-btn"
                onClick={toggleOpp}
                style={showOpp?{background:"var(--darkred)",borderColor:"var(--darkred)",color:"var(--white)",fontWeight:800}:{borderColor:"var(--darkred)",color:"var(--darkred)"}}>
                🔴 {showOpp?"Hide Opp":"Add Opposition"}
              </button>
              <button className="tb-tool-btn" onClick={()=>setShowGrid(g=>!g)} style={showGrid?{background:"var(--teal)",borderColor:"var(--teal)",color:"var(--white)"}:{}}>
                ⊞ Grid
              </button>
              <button className="tb-tool-btn" onClick={exportSummary}>⬇ Export</button>
              <button
                className="tb-tool-btn"
                onClick={()=>{setShowSavePanel(s=>!s); if(!showSavePanel) fetchCloudBoards();}}
                style={{background:"var(--mustard)",borderColor:"var(--mustard)",color:"var(--ink)",fontWeight:800}}>
                {saveStatus==="saving"?"Saving…":saveStatus==="saved"?"✓ Saved":"💾 Save"}
              </button>
            </div>
          </div>

          {/* SAVE / LOAD PANEL */}
          {showSavePanel&&(
            <div style={{background:"var(--ink)",borderBottom:"2px solid var(--mustard)",padding:"0.85rem 1rem",display:"flex",flexDirection:"column",gap:"0.75rem"}}>
              {/* Save as */}
              <div style={{display:"flex",gap:"0.5rem",alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.5)"}}>Save As</span>
                <input
                  value={boardName}
                  onChange={e=>setBoardName(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&saveBoard(boardName)}
                  style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderBottom:"2px solid var(--mustard)",color:"var(--white)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,padding:"0.38rem 0.65rem",outline:"none",flex:1,minWidth:160,maxWidth:280}}
                  placeholder="Board name…"
                />
                <button
                  onClick={()=>saveBoard(boardName)}
                  disabled={saveStatus==="saving"}
                  style={{padding:"0.38rem 0.9rem",background:"var(--mustard)",color:"var(--ink)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.68rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer",flexShrink:0}}>
                  {saveStatus==="saving"?"Saving…":saveStatus==="saved"?"✓ Saved!":"Save Board"}
                </button>
              </div>

              {/* Saved boards list */}
              {(savedBoards.length>0||cloudBoards.length>0)&&(
                <div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginBottom:"0.45rem"}}>
                    Saved Boards {loadingCloud&&<span style={{opacity:0.5}}>· Loading cloud…</span>}
                  </div>
                  <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
                    {/* Local boards */}
                    {savedBoards.map((b,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:"0",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)"}}>
                        <button
                          onClick={()=>loadBoard(b)}
                          style={{padding:"0.32rem 0.65rem",background:"transparent",border:"none",color:b.name===boardName?"var(--mustard)":"rgba(255,255,255,0.7)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                          {b.name}
                          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.56rem",color:"rgba(255,255,255,0.3)",marginLeft:"0.35rem",fontWeight:400}}>
                            {b.formation}
                          </span>
                        </button>
                        <button
                          onClick={()=>deleteBoard(b.name)}
                          style={{padding:"0.32rem 0.45rem",background:"transparent",border:"none",borderLeft:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:"0.65rem",lineHeight:1}}
                          title="Delete">✕</button>
                      </div>
                    ))}
                    {/* Cloud boards not already in local */}
                    {cloudBoards.filter(cb=>!savedBoards.find(lb=>lb.name===cb.name)).map((b,i)=>(
                      <button
                        key={`cloud-${i}`}
                        onClick={()=>loadBoard({...b,tokens:b.tokens||[],arrows:b.arrows||[]})}
                        style={{padding:"0.32rem 0.65rem",background:"rgba(46,125,122,0.3)",border:"1px solid var(--teal)",color:"var(--teal)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:"0.3rem"}}>
                        ☁️ {b.name}
                        <span style={{fontSize:"0.56rem",color:"rgba(255,255,255,0.3)",fontWeight:400}}>{b.formation}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* The pitch */}
          <div
            className={`tb-pitch ${tool==="select"?"select-mode":"arrow-mode"}`}
            ref={pitchRef}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={onDropPitch}
            onClick={onPitchClick}
            onMouseMove={onPitchMouseMove}
          >
            {/* Drop highlight */}
            <div className={`tb-drop-highlight ${dragOver?"active":""}`}/>

            {/* Pitch SVG markings */}
            <svg className="tb-pitch-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Pitch outline */}
              <rect x="5" y="3" width="90" height="94" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.5"/>
              {/* Halfway line */}
              <line x1="5" y1="50" x2="95" y2="50" stroke="rgba(255,255,255,0.35)" strokeWidth="0.4"/>
              {/* Centre circle */}
              <circle cx="50" cy="50" r="12" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.4"/>
              {/* Centre spot */}
              <circle cx="50" cy="50" r="0.8" fill="rgba(255,255,255,0.4)"/>
              {/* Top penalty box */}
              <rect x="25" y="3" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.4"/>
              {/* Top 6-yard box */}
              <rect x="35" y="3" width="30" height="7" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.35"/>
              {/* Top penalty spot */}
              <circle cx="50" cy="14" r="0.6" fill="rgba(255,255,255,0.35)"/>
              {/* Bottom penalty box */}
              <rect x="25" y="81" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.4"/>
              {/* Bottom 6-yard box */}
              <rect x="35" y="90" width="30" height="7" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.35"/>
              {/* Bottom penalty spot */}
              <circle cx="50" cy="86" r="0.6" fill="rgba(255,255,255,0.35)"/>
              {/* Goals */}
              <rect x="42" y="1" width="16" height="3" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5"/>
              <rect x="42" y="96" width="16" height="3" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5"/>
              {/* Grid overlay */}
              {showGrid && [20,30,40,50,60,70,80].map(x=>(
                <line key={`v${x}`} x1={x} y1="3" x2={x} y2="97" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" strokeDasharray="1,2"/>
              ))}
              {showGrid && [15,25,35,45,55,65,75,85].map(y=>(
                <line key={`h${y}`} x1="5" y1={y} x2="95" y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" strokeDasharray="1,2"/>
              ))}
              {/* Attacking direction indicator */}
              <text x="97" y="50" fontSize="2.5" fill="rgba(255,255,255,0.2)" textAnchor="middle" transform="rotate(90,97,50)">ATTACKING →</text>
            </svg>

            {/* Arrow canvas */}
            <canvas ref={canvasRef} className="tb-arrow-canvas"/>

            {/* Empty board prompt — shown when no players assigned */}
            {tokens.length > 0 && tokens.every(t => !t.player) && (
              <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",background:"rgba(0,0,0,0.65)",borderRadius:4,padding:"0.85rem 1.2rem",textAlign:"center",pointerEvents:"none",zIndex:10,maxWidth:220}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--mustard)",marginBottom:"0.3rem"}}>Formation Set</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"rgba(255,255,255,0.7)",fontWeight:300,lineHeight:1.55}}>Drag players from the left panel onto position tokens to assign them</div>
              </div>
            )}

            {/* Player tokens */}
            {tokens.map(token => {
              const rolePoints = token.role ? (ROLE_DATA[token.role] || []) : [];
              const isHovered = hoveredToken === token.id;
              return (
                <div
                  key={token.id}
                  className={`tb-token ${selectedToken===token.id?"selected":""}`}
                  style={{left:`${token.x}%`, top:`${token.y}%`}}
                  onMouseDown={e=>onTokenMouseDown(e,token.id)}
                  onMouseEnter={()=>setHoveredToken(token.id)}
                  onMouseLeave={()=>setHoveredToken(null)}
                  onClick={e=>{
                    e.stopPropagation();
                    if(tool==="select"){
                      if(e.shiftKey || e.altKey){
                        cycleRole(e, token.id);
                      } else {
                        setSelectedToken(token.id);
                        setRightTab("player");
                      }
                    }
                  }}
                >
                  {/* Role tooltip on hover */}
                  {isHovered && token.role && rolePoints.length > 0 && (
                    <div className={`tb-role-tooltip${token.x > 60 ? " flip-left" : ""}`}>
                      <div className="tb-role-tooltip-name">{token.role}</div>
                      {rolePoints.map((pt,i) => (
                        <div key={i} className="tb-role-tooltip-point">{pt}</div>
                      ))}
                      <div className="tb-role-tooltip-hint">Shift+click or ⟳ to cycle role</div>
                    </div>
                  )}
                  {isHovered && !token.role && (
                    <div className={`tb-role-tooltip${token.x > 60 ? " flip-left" : ""}`}>
                      <div className="tb-role-tooltip-name" style={{color:"rgba(255,255,255,0.4)"}}>{token.pos} — No Role</div>
                      <div className="tb-role-tooltip-hint" style={{color:"rgba(255,255,255,0.4)",borderTop:"none",paddingTop:0}}>Apply a system preset to assign roles automatically, or Shift+click to cycle through roles for this position.</div>
                    </div>
                  )}

                  {token.role && <div className="tb-token-role-badge">{token.role.split(" ").slice(0,2).join(" ")}</div>}
                  <div className={`tb-token-circle team-${token.team}`} style={!token.player?{border:"2px dashed rgba(255,255,255,0.45)",background:"rgba(255,255,255,0.08)"}:{}}>
                    {token.player ? (
                      <>
                        <div className="tb-token-num">{token.num}</div>
                        <div className="tb-token-pos">{token.pos}</div>
                      </>
                    ) : (
                      <div className="tb-token-pos" style={{fontSize:"0.72rem",opacity:0.9,fontWeight:800,color:"rgba(255,255,255,0.85)"}}>{token.pos}</div>
                    )}
                  </div>

                  {/* Click-to-cycle role button */}
                  {isHovered && (
                    <div
                      onMouseDown={e=>e.stopPropagation()}
                      onClick={e=>cycleRole(e, token.id)}
                      style={{position:"absolute",top:-28,right:-8,background:"var(--teal)",color:"var(--white)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.48rem",fontWeight:800,padding:"0.12rem 0.35rem",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer",whiteSpace:"nowrap",zIndex:60,border:"1px solid rgba(255,255,255,0.3)"}}
                    >
                      ⟳ Role
                    </div>
                  )}

                  <div className="tb-token-label">
                    {token.player ? token.player.name.split(" ")[0] : <span style={{opacity:0.5,fontStyle:"italic",fontSize:"0.58rem"}}>empty</span>}
                  </div>
                </div>
              );
            })}

            {/* ══ OPPOSITION TOKENS ══ */}
            {showOpp && oppTokens.map(token => {
              const isHovOpp = hoveredOppToken === token.id;
              const isSelOpp = selectedOppToken === token.id;
              return (
                <div
                  key={token.id}
                  className={`tb-token${isSelOpp?" selected":""}`}
                  style={{left:`${token.x}%`, top:`${token.y}%`, zIndex: isHovOpp ? 55 : 9}}
                  onMouseDown={e=>onOppTokenMouseDown(e,token.id)}
                  onMouseEnter={()=>setHoveredOppToken(token.id)}
                  onMouseLeave={()=>setHoveredOppToken(null)}
                  onClick={e=>{e.stopPropagation(); setSelectedOppToken(token.id);}}
                >
                  {/* Opp role tooltip */}
                  {isHovOpp && token.role && (
                    <div className={`tb-role-tooltip${token.x > 60 ? " flip-left" : ""}`} style={{borderColor:"var(--darkred)"}}>
                      <div className="tb-role-tooltip-name" style={{color:"#f87171"}}>{token.role}</div>
                      {(ROLE_DATA[token.role]||[]).map((pt,i) => (
                        <div key={i} className="tb-role-tooltip-point">{pt}</div>
                      ))}
                      <div className="tb-role-tooltip-hint">Opposition · {token.pos}</div>
                    </div>
                  )}

                  <div className="tb-token-circle team-b" style={isSelOpp?{boxShadow:"0 0 0 3px #f87171, 0 0 0 5px rgba(248,113,113,0.4)"}:{}}>
                    <div className="tb-token-num">{token.num}</div>
                    <div className="tb-token-pos">{token.pos}</div>
                  </div>

                  <div className="tb-token-label" style={{background:"rgba(139,42,42,0.85)"}}>
                    {token.role ? token.role.split(" ")[0] : token.pos}
                  </div>
                </div>
              );
            })}
            {cones.map(cone => (
              <div key={cone.id} className="tb-cone" style={{left:`${cone.x}%`,top:`${cone.y}%`}}
                onClick={e=>{e.stopPropagation();setCones(prev=>prev.filter(c=>c.id!==cone.id));}}>
                🔶
              </div>
            ))}

            {/* Text annotations */}
            {annotations.map(ann => (
              <div key={ann.id} className="tb-annotation" style={{left:`${ann.x}%`,top:`${ann.y}%`}}
                onClick={e=>{e.stopPropagation();setAnnotations(prev=>prev.filter(a=>a.id!==ann.id));}}>
                {ann.text}
              </div>
            ))}

            {/* Tool hint */}
            {tool==="arrow"&&!arrowStart&&(
              <div style={{position:"absolute",bottom:"0.8rem",left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"var(--mustard)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,padding:"0.3rem 1rem",letterSpacing:"1.5px",textTransform:"uppercase",pointerEvents:"none"}}>
                CLICK TO SET ARROW START POINT
              </div>
            )}
            {tool==="arrow"&&arrowStart&&(
              <div style={{position:"absolute",bottom:"0.8rem",left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#4ade80",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,padding:"0.3rem 1rem",letterSpacing:"1.5px",textTransform:"uppercase",pointerEvents:"none"}}>
                NOW CLICK THE END POINT
              </div>
            )}
            {tool==="cone"&&(
              <div style={{position:"absolute",bottom:"0.8rem",left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#fb923c",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,padding:"0.3rem 1rem",letterSpacing:"1.5px",textTransform:"uppercase",pointerEvents:"none"}}>
                CLICK TO PLACE CONE · CLICK CONE TO REMOVE
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className="tb-right">
          <div className="tb-right-tabs">
            <div className={`tb-right-tab ${rightTab==="player"?"on":""}`} onClick={()=>setRightTab("player")}>Player</div>
            <div className={`tb-right-tab ${rightTab==="session"?"on":""}`} onClick={()=>setRightTab("session")}>Session</div>
            <div className={`tb-right-tab ${rightTab==="board"?"on":""}`} onClick={()=>setRightTab("board")}>Board</div>
          </div>

          <div className="tb-right-content">

            {/* ── PLAYER TAB ── */}
            {rightTab==="player"&&(
              selectedTokenData ? (
                <>
                  <div className="tb-player-detail-header">
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                      <div>
                        <div className="tb-player-detail-name">
                          {selectedTokenData.player?.name || selectedTokenData.pos}
                        </div>
                        <div className="tb-player-detail-meta">
                          {selectedTokenData.pos} · {selectedTokenData.player?.club||"Position"} · {selectedTokenData.player?.nat||""}
                        </div>
                      </div>
                      {selectedTokenData.player&&<div className="tb-player-detail-ovr">{selectedTokenData.player.overall}</div>}
                    </div>
                  </div>

                  {/* Attributes */}
                  {selectedTokenData.player&&(
                    <>
                      <span className="tb-sec-label">Attributes</span>
                      <div className="tb-mini-attrs">
                        {[["PAC",selectedTokenData.player.pace],["SHO",selectedTokenData.player.shooting],["PAS",selectedTokenData.player.passing],["DRI",selectedTokenData.player.dribbling],["DEF",selectedTokenData.player.defending],["PHY",selectedTokenData.player.physical]].map(([k,v])=>(
                          <div key={k} className="tb-mini-attr">
                            <span className="tb-mini-l">{k}</span>
                            <span className={`tb-mini-v ${attrC(v)}`}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Role assignment */}
                  <span className="tb-sec-label">Assign Role</span>
                  <select className="tb-role-select"
                    value={selectedTokenData.role||""}
                    onChange={e=>assignRole(selectedTokenData.id, e.target.value)}>
                    <option value="">— Select a role —</option>
                    {Object.keys(ROLE_DATA).map(r=><option key={r}>{r}</option>)}
                  </select>

                  {/* Role responsibilities */}
                  {selectedTokenData.role && ROLE_DATA[selectedTokenData.role] && (
                    <>
                      <span className="tb-sec-label">Responsibilities</span>
                      {ROLE_DATA[selectedTokenData.role].map((r,i)=>(
                        <div key={i} className="tb-resp-item">{r}</div>
                      ))}
                    </>
                  )}

                  {/* Drills for role */}
                  {selectedTokenData.role && ROLE_DRILLS[selectedTokenData.role] && (
                    <>
                      <span className="tb-sec-label">Recommended Drills</span>
                      {ROLE_DRILLS[selectedTokenData.role].map((d,i)=>(
                        <div key={i} className="tb-drill-attached">
                          <div className="tb-drill-title">{d}</div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Player notes */}
                  <span className="tb-sec-label">Coach Notes</span>
                  <textarea className="tb-notes-area"
                    placeholder={`Instructions for ${selectedTokenData.player?.name||selectedTokenData.pos}...`}
                    value={notes[selectedTokenData.id]||""}
                    onChange={e=>setNotes(prev=>({...prev,[selectedTokenData.id]:e.target.value}))}
                  />

                  {/* Actions */}
                  {selectedTokenData.player&&(
                    <button className="tb-btn-secondary" onClick={()=>removePlayerFromSlot(selectedTokenData.id)}>
                      Remove from Pitch
                    </button>
                  )}
                </>
              ) : (
                <div className="tb-empty">
                  <div className="tb-empty-icon">👆</div>
                  <div className="tb-empty-title">Select a Player</div>
                  <div className="tb-empty-sub">Click any player token on the pitch to assign roles, view responsibilities and attach drills.</div>
                </div>
              )
            )}

            {/* ── SESSION TAB ── */}
            {rightTab==="session"&&(
              <>
                <span className="tb-sec-label">Formation</span>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.8rem",letterSpacing:"3px",color:"var(--ink)",marginBottom:"0.6rem"}}>{formation}</div>

                <span className="tb-sec-label">Session Objectives</span>
                {[
                  {title:"Tactical Shape",desc:`Establish the ${formation} shape in possession and out of possession.`},
                  {title:"Role Clarity",desc:"Each player understands their specific responsibilities in the system."},
                  {title:"Pressing Triggers",desc:"Define and practice the press triggers for this formation."},
                  {title:"Set Pieces",desc:"Rehearse corners and free kicks in the defined formation."},
                ].map((s,i)=>(
                  <div key={i} className="tb-session-card">
                    <div className="tb-session-title">{s.title}</div>
                    <div className="tb-session-desc">{s.desc}</div>
                  </div>
                ))}

                <span className="tb-sec-label">Session Structure</span>
                {[
                  {time:"0-10 min",phase:"Activation",desc:"Rondo 4v1 — keep it loose, warm up joints and establish touch."},
                  {time:"10-30 min",phase:"Shape Work",desc:`Shadow play in the ${formation} — no opposition, focus on spacing and movement.`},
                  {time:"30-50 min",phase:"Press Triggers",desc:"Add opposition. Practice the coordinated press and the recovery shape."},
                  {time:"50-65 min",phase:"Set Pieces",desc:"Attacking and defensive set pieces in the system."},
                  {time:"65-80 min",phase:"Match Practice",desc:"11v11 with the formation and roles applied. Coaching interventions."},
                ].map((s,i)=>(
                  <div key={i} style={{display:"flex",gap:"0.6rem",padding:"0.5rem 0",borderBottom:"1px solid var(--rule2)"}}>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.75rem",letterSpacing:"1px",color:"var(--teal)",width:"52px",flexShrink:0,marginTop:"0.1rem"}}>{s.time}</div>
                    <div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:800,color:"var(--ink)",marginBottom:"0.1rem"}}>{s.phase}</div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,lineHeight:1.55}}>{s.desc}</div>
                    </div>
                  </div>
                ))}

                <span className="tb-sec-label" style={{marginTop:"0.9rem"}}>Session Notes</span>
                <textarea className="tb-notes-area" placeholder="Overall session objectives and coaching focus for today..."
                  value={notes["session"]||""}
                  onChange={e=>setNotes(prev=>({...prev,session:e.target.value}))}
                />
                <button className="tb-btn-primary" onClick={exportSummary}>⬇ Export Session Plan</button>
              </>
            )}

            {/* ── BOARD TAB ── */}
            {rightTab==="board"&&(
              <>
                <span className="tb-sec-label">Board Summary</span>
                <div style={{background:"var(--cream2)",border:"1.5px solid var(--rule)",padding:"0.85rem",marginBottom:"0.9rem"}}>
                  {[
                    {label:"Formation",val:formation},
                    {label:"Players placed",val:`${pitchPlayers.length}/11`},
                    {label:"Roles assigned",val:`${tokens.filter(t=>t.role).length}`},
                    {label:"Arrows drawn",val:`${arrows.length}`},
                    {label:"Cones placed",val:`${cones.length}`},
                    {label:"Notes written",val:`${Object.values(notes).filter(Boolean).length}`},
                  ].map((r,i)=>(
                    <div key={i} className="tb-summary-row">
                      <span className="tb-summary-label">{r.label}</span>
                      <span className="tb-summary-val">{r.val}</span>
                    </div>
                  ))}
                </div>

                <span className="tb-sec-label">Player Assignments</span>
                {tokens.map(t=>(
                  <div key={t.id} style={{display:"flex",alignItems:"center",gap:"0.55rem",padding:"0.42rem 0",borderBottom:"1px solid var(--rule2)"}}>
                    <span className={`tb-pool-pos sp-pos-badge ${posGroup(t.pos)}`} style={{fontSize:"0.54rem",padding:"0.1rem 0.38rem"}}>{t.pos}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.76rem",fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.player?.name||<span style={{color:"var(--muted)",fontStyle:"italic",fontWeight:300}}>Empty</span>}</div>
                      {t.role&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--teal)",fontStyle:"italic"}}>{t.role}</div>}
                    </div>
                    {notes[t.id]&&<div title={notes[t.id]} style={{fontSize:"0.6rem",color:"var(--mustard2)"}}>📝</div>}
                  </div>
                ))}

                <span className="tb-sec-label" style={{marginTop:"0.9rem"}}>Quick Actions</span>
                <button className="tb-btn-primary" onClick={exportSummary}>⬇ Export Tactics Sheet</button>
                <button className="tb-btn-secondary" onClick={()=>{setArrows([]);setCones([]);setAnnotations([]);}}>Clear Overlays</button>
                <button className="tb-btn-secondary" onClick={()=>{setTokens(prev=>prev.map(t=>({...t,role:null})));setNotes({});}}>Reset Roles & Notes</button>
                <button className="tb-btn-secondary" style={{borderColor:"var(--darkred)",color:"var(--darkred)"}} onClick={()=>{setTokens(prev=>prev.map(t=>({...t,player:null,role:null})));setArrows([]);setCones([]);setAnnotations([]);setNotes({});setSelectedToken(null);}}>
                  Clear Entire Board
                </button>

                <span className="tb-sec-label" style={{marginTop:"0.9rem"}}>
                  System Presets
                  <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.55rem",fontWeight:400,letterSpacing:0,textTransform:"none",color:"var(--teal)",marginLeft:"0.5rem"}}>Matching {formation} highlighted</span>
                </span>

                {/* Opposition team section */}
                <div style={{background:"rgba(139,42,42,0.1)",border:"1.5px solid rgba(139,42,42,0.3)",padding:"0.75rem 0.85rem",marginBottom:"0.75rem"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.55rem"}}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--darkred)"}}>
                      🔴 Opposition Team
                    </div>
                    <button onClick={toggleOpp} className={`tb-opp-toggle${showOpp?" on":""}`}>
                      {showOpp?"Visible":"Hidden"}
                    </button>
                  </div>

                  {showOpp && (
                    <>
                      {/* Active opp system pill */}
                      {oppSystem ? (
                        <div style={{background:"rgba(139,42,42,0.3)",border:"1px solid var(--darkred)",padding:"0.45rem 0.65rem",marginBottom:"0.55rem",display:"flex",alignItems:"center",gap:"0.4rem"}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:oppSystem.color,flexShrink:0,border:oppSystem.color==="#FFFFFF"?"1px solid #ccc":""}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:800,color:"rgba(255,255,255,0.9)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{oppSystem.name}</div>
                            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.68rem",letterSpacing:"1px",color:"rgba(255,255,255,0.45)",marginTop:"0.05rem"}}>{oppSystem.formation} · {oppSystem.category}</div>
                          </div>
                          <button onClick={()=>setOppSystem(null)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:"0.7rem",flexShrink:0}}>✕</button>
                        </div>
                      ) : (
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"0.5rem"}}>
                          No system selected — pick from the library below
                        </div>
                      )}

                      {/* Formation picker (still useful for manual drag positioning) */}
                      <div style={{display:"flex",alignItems:"center",gap:"0.5rem",marginBottom:"0.5rem"}}>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:700,color:"var(--muted)",flexShrink:0}}>Formation</div>
                        <select
                          value={oppFormation}
                          onChange={e=>{setOppFormation(e.target.value);applyOppFormation(e.target.value);setOppSystem(null);}}
                          style={{flex:1,background:"rgba(0,0,0,0.4)",border:"1px solid rgba(139,42,42,0.4)",color:"rgba(255,255,255,0.85)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:700,padding:"0.3rem 0.5rem",outline:"none",cursor:"pointer"}}>
                          {Object.keys(FORMATIONS).map(f=><option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>

                      {/* Search + full system library */}
                      <OppSystemPicker
                        oppFormation={oppFormation}
                        oppSystem={oppSystem}
                        onPick={applyOppSystem}
                      />

                      <button
                        onClick={()=>{setOppTokens([]);setOppSystem(null);}}
                        style={{width:"100%",marginTop:"0.55rem",padding:"0.32rem",background:"transparent",border:"1px solid rgba(139,42,42,0.4)",color:"var(--darkred)",fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:"0.62rem",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer"}}>
                        Clear Opposition
                      </button>
                    </>
                  )}

                  {!showOpp && (
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",lineHeight:1.55}}>
                      Toggle on to place an opposition formation on the pitch. Pick any system to set their shape and roles instantly.
                    </div>
                  )}
                </div>

                {/* Current system info */}
                {currentSystem&&(
                  <div style={{background:"var(--teal4)",border:"1.5px solid var(--teal)",borderLeft:"4px solid var(--teal)",padding:"0.75rem 0.85rem",marginBottom:"0.75rem"}}>
                    <div style={{display:"flex",alignItems:"center",gap:"0.45rem",marginBottom:"0.35rem"}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:currentSystem.color,flexShrink:0,border:currentSystem.color==="#FFFFFF"?"1px solid #ccc":""}}/>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.85rem",letterSpacing:"1.5px",color:"var(--ink)"}}>{currentSystem.name}</div>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.72rem",letterSpacing:"1px",color:"var(--teal)",marginLeft:"auto"}}>{currentSystem.formation}</span>
                    </div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--ink2)",fontWeight:300,lineHeight:1.6,marginBottom:"0.5rem",fontStyle:"italic"}}>{currentSystem.description}</div>
                    {currentSystem.principles&&(
                      <div>
                        {currentSystem.principles.map((p,i)=>(
                          <div key={i} style={{display:"flex",gap:"0.35rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--ink2)",marginBottom:"0.2rem",lineHeight:1.45}}>
                            <span style={{color:"var(--teal)",fontWeight:800,flexShrink:0}}>→</span>{p}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {["Possession","High Press","Counter-attack","Low Block","Direct"].map(cat=>{
                  const matching = SYSTEM_PRESETS.filter(s=>s.category===cat && s.formation===formation);
                  const others = SYSTEM_PRESETS.filter(s=>s.category===cat && s.formation!==formation);
                  if(!matching.length && !others.length) return null;
                  return(
                    <div key={cat} style={{marginBottom:"0.5rem"}}>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.5rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",margin:"0.3rem 0 0.2rem"}}>{cat}</div>
                      {matching.map(s=>(
                        <button key={s.name} className={`tb-system-btn ${currentSystem?.name===s.name?"active":""}`} onClick={()=>applySystem(s)} style={{marginBottom:"0.2rem"}}>
                          <div className="tb-system-dot" style={{background:s.color,border:s.color==="#FFFFFF"?"1px solid #ccc":""}}/>
                          <div style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:700,color:"var(--ink)"}}>{s.name}</div>
                        </button>
                      ))}
                      {others.map(s=>(
                        <button key={s.name} className={`tb-system-btn`} onClick={()=>applySystem(s)} style={{marginBottom:"0.2rem",opacity:0.55}}>
                          <div className="tb-system-dot" style={{background:s.color,border:s.color==="#FFFFFF"?"1px solid #ccc":""}}/>
                          <div style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",fontWeight:500,color:"var(--muted)"}}>{s.name}</div>
                          <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.6rem",color:"var(--muted)",flexShrink:0}}>{s.formation}</span>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
